// src/components/EditLoadBearers.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "../axiosConfig";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import "../styles/EditLoadBearers.css";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { useTranslation } from "react-i18next";

const CURRENCY_OPTIONS = [
  { value: "EUR", label: "EUR" },
  { value: "USD", label: "USD" },
  { value: "GBP", label: "GBP" },
];

const formatInputDate = (isoDate) => {
  if (!isoDate) return "";
  const dateObj = new Date(isoDate);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const uniqBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter((x) => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const makeOption = (value, label, fallback = "Item") =>
  value ? { value, label: label || `${fallback} ${value}` } : null;

// === DEDUPE HELPERS ===
const norm = (s) => String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
const keyOfPlace = (p) =>
  [
    norm(p.placeName || p.location),
    norm(p.address),
    norm(p.postalCode),
    norm(p.city),
    norm(p.country),
  ].join("|");

const scoreOfPlace = (p) =>
  (p.address ? 1 : 0) +
  (p.postalCode ? 1 : 0) +
  (p.city ? 1 : 0) +
  (p.country ? 1 : 0) +
  (p.gMapsLink ? 1 : 0) +
  (p.coordinates ? 1 : 0) +
  (p.contactPhone ? 1 : 0) +
  (p.workingHours ? 1 : 0);

const MAX_LENGTH = 13.6;
const MAX_HEIGHT = 2.7;
const MAX_WIDTH = 2.5;
const MIN_LENGTH_NORMAL = 0.01;
const MAX_POINTS = 7;

const toId = (v) => (v == null ? "" : String(v));

const displayAuditEmail = (value) => {
  const email = String(value || "").trim();
  return email || "—";
};

function EditLoadBearers() {
  const { t } = useTranslation("edit_load_bearers");

  // 🆕 Loading access options
  const loadingAccessOptions = useMemo(
    () => [
      { value: "SIDE", label: t("loadingAccess.side", { defaultValue: "Side" }) },
      { value: "REAR", label: t("loadingAccess.rear", { defaultValue: "Rear" }) },
      { value: "TOP", label: t("loadingAccess.top", { defaultValue: "Top" }) },
      { value: "DOCK", label: t("loadingAccess.dock", { defaultValue: "Dock" }) },
      { value: "ANY", label: t("loadingAccess.any", { defaultValue: "Any" }) },
    ],
    [t]
  );

  // 🆕 Handling equipment options
  const handlingEquipmentOptions = useMemo(
    () => [
      { value: "FORKLIFT", label: t("handlingEquipment.forklift", { defaultValue: "Forklift" }) },
      { value: "PALLET_JACK", label: t("handlingEquipment.palletJack", { defaultValue: "Pallet jack" }) },
      { value: "ELECTRIC_PALLET_JACK", label: t("handlingEquipment.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
      { value: "CRANE", label: t("handlingEquipment.crane", { defaultValue: "Crane" }) },
      { value: "LOADING_RAMP", label: t("handlingEquipment.loadingRamp", { defaultValue: "Loading ramp" }) },
      { value: "TAIL_LIFT", label: t("handlingEquipment.tailLift", { defaultValue: "Tail lift" }) },
      { value: "HAND_LOAD", label: t("handlingEquipment.handLoad", { defaultValue: "Hand load" }) },
      { value: "CONVEYOR", label: t("handlingEquipment.conveyor", { defaultValue: "Conveyor" }) },
      { value: "OTHER", label: t("handlingEquipment.other", { defaultValue: "Other" }) },
    ],
    [t]
  );

  // 🆕 Trailer Type options (KEEP IN SYNC with backend isIn)
  const trailerTypeOptions = useMemo(
    () => [
      { value: "ANY", label: t("trailerType.any", { defaultValue: "Any" }) },

      { value: "TAUTLINER", label: t("trailerType.tautliner", { defaultValue: "Tautliner / Curtain" }) },
      { value: "REEFER", label: t("trailerType.reefer", { defaultValue: "Reefer / Frigo" }) },
      { value: "BOX", label: t("trailerType.box", { defaultValue: "Box" }) },
      { value: "FLATBED", label: t("trailerType.flatbed", { defaultValue: "Flatbed" }) },
      { value: "LOWBED", label: t("trailerType.lowbed", { defaultValue: "Lowbed" }) },
      { value: "TANKER", label: t("trailerType.tanker", { defaultValue: "Tanker" }) },
      { value: "TIPPER", label: t("trailerType.tipper", { defaultValue: "Tipper" }) },
      { value: "CONTAINER_CHASSIS", label: t("trailerType.containerChassis", { defaultValue: "Container chassis" }) },
      { value: "MEGA", label: t("trailerType.mega", { defaultValue: "Mega" }) },

      { value: "OTHER", label: t("trailerType.other", { defaultValue: "Other" }) },
    ],
    [t]
  );

  const handleHandlingEquipmentChange = (index, opt, isPickup) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    if (isPickup) {
      setPickUpPlaces((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          handlingEquipment: value,
          // se mudou de OTHER, limpa o texto
          handlingEquipmentOther: value === "OTHER" ? (next[index].handlingEquipmentOther || "") : "",
          handlingEquipmentError: false,
          handlingEquipmentOtherError: false,
        };
        return next;
      });
    } else {
      setDeliveryPlaces((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          handlingEquipment: value,
          handlingEquipmentOther: value === "OTHER" ? (next[index].handlingEquipmentOther || "") : "",
          handlingEquipmentError: false,
          handlingEquipmentOtherError: false,
        };
        return next;
      });
    }
  };

  const handleHandlingEquipmentOtherChange = (index, e, isPickup) => {
    const v = e?.target?.value ?? "";

    if (isPickup) {
      setPickUpPlaces((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          handlingEquipmentOther: v,
          handlingEquipmentOtherError: false,
        };
        return next;
      });
    } else {
      setDeliveryPlaces((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          handlingEquipmentOther: v,
          handlingEquipmentOtherError: false,
        };
        return next;
      });
    }
  };

  // 🆕 Trailer type handlers (load-level)
  const handleTrailerTypeChange = (opt) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    setTrailerType(value);
    setTrailerTypeError("");

    // se sai de OTHER, limpa o texto
    if (value !== "OTHER") {
      setTrailerTypeOther("");
      setTrailerTypeOtherError("");
    } else {
      // mantém o texto existente (se houver)
      setTrailerTypeOther((prev) => prev || "");
      setTrailerTypeOtherError("");
    }
  };

  const handleTrailerTypeOtherChange = (e) => {
    const v = e?.target?.value ?? "";
    setTrailerTypeOther(v);
    setTrailerTypeOtherError("");
  };

  // 🆕 Change handler
  const handleLoadingAccessChange = (index, opt, isPickup) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    if (isPickup) {
      setPickUpPlaces((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], loadingAccess: value, loadingAccessError: false };
        return next;
      });
    } else {
      setDeliveryPlaces((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], loadingAccess: value, loadingAccessError: false };
        return next;
      });
    }
  };

  const { loadId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const initialLoad = state?.load ?? state?.prefill ?? null;
  const [ready, setReady] = useState(!!initialLoad);

  const mapPlace = (p = {}) => ({
    location: p.location || p.placeName || "",
    date: formatInputDate(p.date || ""),
    hour: p.hour || "",
  
    // 🆕 point-level dimensions
    length: p.length ?? "",
    width: p.width ?? "",
    height: p.height ?? "",
  
    placeId: p.placeId ?? p.id ?? null,
  
    address: p.address || "",
    postalCode: p.postalCode || "",
    city: p.city || "",
    country: p.country || "",
    gMapsLink: p.gMapsLink || "",
    coordinates: p.coordinates || "",
    contactPhone: p.contactPhone || "",
  
    // 🆕 Reference (optional)
    reference: p.reference || "",
  
    loadingAccess: p.loadingAccess != null
      ? String(p.loadingAccess).trim().toUpperCase()
      : null,
    loadingAccessError: false,
  
    // 🆕 Handling equipment
    handlingEquipment:
      p.handlingEquipment != null ? String(p.handlingEquipment).trim().toUpperCase() : null,
    handlingEquipmentOther: p.handlingEquipmentOther || "",
    handlingEquipmentError: false,
    handlingEquipmentOtherError: false,
  
    // 🆕 point-level dimension errors
    pointLengthError: false,
    pointWidthError: false,
    pointHeightError: false,
  
    showMoreFields: false,
  });

  const initialPickupsRaw =
    (initialLoad?.pickupLocations?.length
      ? initialLoad.pickupLocations
      : initialLoad?.pickupPoints) || [];

  const initialDeliveriesRaw =
    (initialLoad?.deliveryLocations?.length
      ? initialLoad.deliveryLocations
      : initialLoad?.deliveryPoints) || [];

  const [load, setLoad] = useState(initialLoad);

  const [length, setLength] = useState(() => initialLoad?.length ?? "");
  const [height, setHeight] = useState(() => initialLoad?.height ?? "");
  const [width, setWidth] = useState(() => initialLoad?.width ?? "");

  // 🆕 Weight (required)
  const [weight, setWeight] = useState(() => initialLoad?.weight ?? "");
  const [weightError, setWeightError] = useState("");
  
  // 🆕 Merchandise (required)
  const [merchandise, setMerchandise] = useState(() => initialLoad?.merchandise ?? "");
  const [merchandiseError, setMerchandiseError] = useState("");

  // 🆕 Trailer Type (load-level)
  const [trailerType, setTrailerType] = useState(() =>
    initialLoad?.trailerType != null
      ? String(initialLoad.trailerType).trim().toUpperCase()
      : null
  );
  const [trailerTypeOther, setTrailerTypeOther] = useState(
    () => initialLoad?.trailerTypeOther ?? ""
  );
  const [trailerTypeError, setTrailerTypeError] = useState("");
  const [trailerTypeOtherError, setTrailerTypeOtherError] = useState("");

  // 💰 Price + Currency
  const [price, setPrice] = useState(() => initialLoad?.price ?? "");
  const [priceCurrency, setPriceCurrency] = useState(
    () => initialLoad?.priceCurrency || "EUR"
  );
  const [priceError, setPriceError] = useState("");

  const [price4subcontracted, setPrice4subcontracted] = useState(
    () => initialLoad?.price4subcontracted ?? initialLoad?.price4subcontrated ?? ""
  );  
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");

  const [notes4driver, setNotes4driver] = useState(
    () => initialLoad?.notes4driver ?? ""
  );
  const [notes4driverError, setNotes4driverError] = useState("");

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
    });

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  // 🆕 Notes for subcontracted (optional, only when external carrier selected)
  const [notes4subcontracted, setNotes4subcontracted] = useState(
    () => initialLoad?.notes4subcontracted ?? ""
  );
  const [notes4subcontractedError, setNotes4subcontractedError] = useState("");

  // 🆕 Subcontracted Emails (only when external carrier selected, max 3)
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState(() => {
    const raw = initialLoad?.subcontractedEmails;
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw
        .map((email) => String(email || "").trim().toLowerCase())
        .filter((email) => isValidEmail(email))
        .map((email) => ({ value: email, label: email }));
    }

    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((email) => String(email || "").trim().toLowerCase())
        .filter((email) => isValidEmail(email))
        .map((email) => ({ value: email, label: email }));
    }

    return [];
  });

  const [maxAllowedLoadBearer, setMaxAllowedLoadBearer] = useState(MAX_LENGTH);
  const [maxAllowedHeight, setMaxAllowedHeight] = useState(MAX_HEIGHT);
  const [loading, setLoading] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [totalSuperImposableLength, setTotalSuperImposableLength] = useState(0);

  const [pickUpPlaces, setPickUpPlaces] = useState(() =>
    initialPickupsRaw.map(mapPlace)
  );
  const [deliveryPlaces, setDeliveryPlaces] = useState(() =>
    initialDeliveriesRaw.map(mapPlace)
  );

  const [pickUpPlaceError, setPickUpPlaceError] = useState("");
  const [deliveryPlaceError, setDeliveryPlaceError] = useState("");
  const [invalidDeliveryIndexes, setInvalidDeliveryIndexes] = useState([]);
  const [existingDateHourConflicts, setExistingDateHourConflicts] = useState([]);
  const [conflictDateHours, setConflictDateHours] = useState(new Set());
  const [conflictError, setConflictError] = useState("");

  const [invalidFields, setInvalidFields] = useState({
    pickups: [],
    deliveries: [],
  });
  const [generalErrors, setGeneralErrors] = useState({
    clientType: "",
    client: "",
  });
  const [pickUpFieldError, setPickUpFieldError] = useState("");
  const [deliveryFieldError, setDeliveryFieldError] = useState("");

  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");

  const dateRefs = useRef([]);
  const hourRefs = useRef([]);
  const deliveryDateRefs = useRef([]);
  const deliveryHourRefs = useRef([]);
  const today = new Date().toISOString().split("T")[0];

  const [clientType, setClientType] = useState(
    () => initialLoad?.clientType || ""
  );
  const [industryClients, setIndustryClients] = useState([]);
  const [carrierClients, setCarrierClients] = useState([]);

  const [industryClientId, setIndustryClientId] = useState("");
  const [carrierClientId, setCarrierClientId] = useState("");

  const [selectedClient, setSelectedClient] = useState(() => {
    if (initialLoad?.clientType === "industry" && initialLoad?.industryClientId)
      return {
        value: initialLoad.industryClientId,
        label:
          initialLoad.industryClientName || t("labels.industryClientFallback"),
      };
    if (initialLoad?.clientType === "carrier" && initialLoad?.carrierClientId)
      return {
        value: initialLoad.carrierClientId,
        label:
          initialLoad.carrierClientName || t("labels.carrierClientFallback"),
      };
    return null;
  });

  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState(() => {
    const raw = initialLoad?.clientContactEmails;
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw
        .map((email) => String(email || "").trim().toLowerCase())
        .filter((email) => isValidEmail(email))
        .map((email) => ({ value: email, label: email }));
    }

    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((email) => String(email || "").trim().toLowerCase())
        .filter((email) => isValidEmail(email))
        .map((email) => ({ value: email, label: email }));
    }

    return [];
  });

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType) return [];

    if (clientType === "industry" && industryClientId) {
      const fullClient = (industryClients || []).find(
        (c) => String(c.id) === String(industryClientId)
      );
      return extractClientEmails(fullClient);
    }

    if (clientType === "carrier" && carrierClientId) {
      const fullClient = (carrierClients || []).find(
        (c) => String(c.id) === String(carrierClientId)
      );
      return extractClientEmails(fullClient);
    }

    return [];
  }, [clientType, industryClientId, carrierClientId, industryClients, carrierClients]);

  const [industryOptions, setIndustryOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [clientTypeError, setClientTypeError] = useState("");
  const [clientError, setClientError] = useState("");
  const setClientSelectionError = (msg) => setClientError(msg);

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads");
  };

  const [dateFieldErrors, setDateFieldErrors] = useState({});
  const [globalHourFormatError, setGlobalHourFormatError] = useState("");
  const [fetchedIndustryClientId, setFetchedIndustryClientId] = useState(null);
  const [fetchedCarrierClientId, setFetchedCarrierClientId] = useState(null);
  const [carrierId, setCarrierId] = useState(() =>
    initialLoad ? (initialLoad?.carrierId != null ? toId(initialLoad.carrierId) : "__internal__") : ""
  );
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");

  // 🆕 fetch companyId + companyName from whoami (tenant)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (!alive) return;

        const cid =
          res.data?.companyId ??
          res.data?.company_id ??
          res.data?.company?.id ??
          res.data?.userCompanyRaw ??
          res.data?.reqCompanyId ??
          null;

        const cname =
          res.data?.company_name ??
          res.data?.companyName ??
          res.data?.company?.name ??
          res.data?.company?.companyName ??
          res.data?.user?.company_name ??
          "";

        setCompanyId(cid != null ? String(cid) : null);
        setCompanyName(String(cname || "").trim());
      } catch (e) {
        console.warn("whoami fetch failed:", e?.response?.status);
        setCompanyId(null);
        setCompanyName("");
      }
    })();

    return () => {
      alive = false;
    };
  }, []);  

  const [carriers, setCarriers] = useState([]);
  const [allPlaces, setAllPlaces] = useState([]);
  const [placeOptions, setPlaceOptions] = useState([]);
  const [transportCarrierError, setTransportCarrierError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [deliveryBeforePickupErrors, setDeliveryBeforePickupErrors] = useState({
    date: [],
    hour: [],
  });
  const [invalidHourFields, setInvalidHourFields] = useState({
    pickups: [],
    deliveries: [],
  });
  const [isFetching, setIsFetching] = useState(false);

  const [industryClientPrefill, setIndustryClientPrefill] = useState(() =>
    initialLoad?.clientType === "industry" && initialLoad?.industryClientId
      ? {
          value: initialLoad.industryClientId,
          label:
            initialLoad.industryClientName ||
            initialLoad.industryClient?.companyName ||
            t("labels.clientFallback"),
        }
      : null
  );

  const [carrierClientPrefill, setCarrierClientPrefill] = useState(() =>
    initialLoad?.clientType === "carrier" && initialLoad?.carrierClientId
      ? {
          value: initialLoad.carrierClientId,
          label:
            initialLoad.carrierClientName ||
            initialLoad.carrierClient?.companyName ||
            t("labels.clientFallback"),
        }
      : null
  );

  const [carrierPrefill, setCarrierPrefill] = useState(() =>
    initialLoad?.carrierId
      ? {
          value: initialLoad.carrierId,
          label:
            initialLoad.carrier?.companyName ||
            initialLoad.carrierName ||
            `${t("labels.carrier")} ${initialLoad.carrierId}`,
        }
      : null
  );

  const formatPointType = (type) => {
    const raw = String(type || "").toUpperCase();
    if (raw === "PICKUP") return t("pointTypes.pickup");
    if (raw === "DELIVERY") return t("pointTypes.delivery");
    if (raw === "STRATEGIC") return t("pointTypes.strategic");
    return type || t("pointTypes.point");
  };

  const currentCarrierOption = useMemo(() => {
    const cid = toId(carrierId);
    if (carriers.length && cid) {
      const c = carriers.find((c) => toId(c.id) === cid);
      if (c) return { value: toId(c.id), label: c.companyName };
    }
    if (carrierPrefill)
      return { value: toId(carrierPrefill.value), label: carrierPrefill.label };
    if (cid) {
      const fallbackLabel =
        load?.carrier?.companyName ||
        load?.carrierName ||
        `${t("labels.carrier")} ${cid}`;
      return { value: cid, label: fallbackLabel };
    }
    return null;
  }, [carriers, carrierId, carrierPrefill, load, t]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ tags sempre numa linha
        flexWrap: "nowrap",

        // ✅ scroll horizontal quando há várias opções
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        // ✅ altura/alinhamento consistente
        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ espaço para clear + seta
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  const carrierOptionsStable = useMemo(() => {
    const base = carriers.map((c) => ({ value: toId(c.id), label: c.companyName }));

    const internalLabel =
      String(companyName || "").trim() ||
      String(load?.carrierName || "").trim() ||
      String(initialLoad?.carrierName || "").trim() ||
      t("labels.internalCarrierFallback", { defaultValue: "Internal carrier" });

    const internalOpt = {
      value: "__internal__",
      label: `${internalLabel} (internal)`,
      isInternal: true,
    };

    const injected = currentCarrierOption ? [currentCarrierOption] : [];
    const withInternal = [internalOpt, ...injected, ...base];

    return uniqBy(withInternal, (o) => String(o.value));
  }, [carriers, currentCarrierOption, companyName, load?.carrierName, initialLoad?.carrierName, t]);

  // 🆕 selected carrier option (needs carrierOptionsStable declared above)
  const selectedTransportOptMemo = useMemo(() => {
    return (
      (carrierOptionsStable || []).find((o) => String(o.value) === String(carrierId)) || null
    );
  }, [carrierOptionsStable, carrierId]);

  // 🆕 internal carrier flag
  const isInternalCarrierMemo = useMemo(() => {
    return selectedTransportOptMemo?.value === "__internal__";
  }, [selectedTransportOptMemo]);

  // 🆕 show notes4subcontracted ONLY if carrierId differs from companyName (tenant)
  const showNotes4subcontracted = useMemo(() => {
    if (!carrierId) return false;
    if (isInternalCarrierMemo) return false;

    const own = String(companyName || "").trim().toLowerCase();
    const sel = String(selectedTransportOptMemo?.label || "").trim().toLowerCase();

    // se não der para comparar, assume external (para não esconder campo indevidamente)
    if (!own || !sel) return true;

    return sel !== own;
  }, [carrierId, isInternalCarrierMemo, companyName, selectedTransportOptMemo]);

  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showNotes4subcontracted || !carrierId || carrierId === "__internal__") return [];

    const fullCarrier = (carrierClients || []).find(
      (c) => String(c.id) === String(carrierId)
    );

    return extractClientEmails(fullCarrier);
  }, [showNotes4subcontracted, carrierId, carrierClients]);

  // ✅ In Edit mode, do NOT auto-overwrite saved clientContactEmails.
  // The selected emails must come from loadData.clientContactEmails.
  // Options can still come from the selected client's contact list.
  
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    if (!selectedSubcontractedEmailOptions.length) {
      setSelectedSubcontractedEmails([]);
      return;
    }
  
    setSelectedSubcontractedEmails((prev) => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return selectedSubcontractedEmailOptions.slice(0, 3);
    });
  }, [showNotes4subcontracted, selectedSubcontractedEmailOptions]);

  useEffect(() => {
    if (!showNotes4subcontracted) {
      setSelectedSubcontractedEmails([]);
      return;
    }

    if (!selectedSubcontractedEmailOptions.length) {
      setSelectedSubcontractedEmails([]);
      return;
    }

    setSelectedSubcontractedEmails((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;

      const allowed = new Set(selectedSubcontractedEmailOptions.map((opt) => String(opt.value)));
      return prev.filter((opt) => allowed.has(String(opt.value))).slice(0, 3);
    });
  }, [showNotes4subcontracted, selectedSubcontractedEmailOptions]);

  // 🧼 if user switches back to internal carrier, clear subcontracted notes
  useEffect(() => {
    if (!showNotes4subcontracted) {
      setNotes4subcontracted("");
      setNotes4subcontractedError("");

      setSelectedSubcontractedEmails([]);

      setPrice4subcontracted("");
      setPrice4subcontractedError("");
    }
  }, [showNotes4subcontracted]);

  const tsCounter = useMemo(() => {
    return (
      load?.transportSession?.transportSessionNumber ??
      load?.transportSessionNumber ??
      state?.load?.transportSession?.transportSessionNumber ??
      state?.load?.transportSessionNumber ??
      state?.prefill?.transportSession?.transportSessionNumber ??
      state?.prefill?.transportSessionNumber ??
      state?.prefill?.tsUiCounter ??
      load?.tsUiCounter ??
      null
    );
  }, [load, state]);

  const loadCounter = useMemo(() => {
    return (
      load?.loadNumber ??
      state?.load?.loadNumber ??
      state?.prefill?.loadNumber ??
      state?.prefill?.uiCounter ??
      load?.uiCounter ??
      null
    );
  }, [load, state]);

  const [prefillPlaceOptions, setPrefillPlaceOptions] = useState(() => {
    const fromPickups = (initialPickupsRaw || [])
      .map((p) => ({
        value: String(p.placeId ?? p.id ?? p.location ?? ""),
        label: p.location || p.placeName || "",
        fullData: p.place || null,
      }))
      .filter((o) => o.value && o.label);

    const fromDeliveries = (initialDeliveriesRaw || [])
      .map((p) => ({
        value: String(p.placeId ?? p.id ?? p.location ?? ""),
        label: p.location || p.placeName || "",
        fullData: p.place || null,
      }))
      .filter((o) => o.value && o.label);

    return uniqBy([...(fromPickups || []), ...(fromDeliveries || [])], (o) =>
      String(o.value)
    );
  });

  useEffect(() => {
    deliveryDateRefs.current = deliveryPlaces.map(
      (_, i) => deliveryDateRefs.current[i] || null
    );
    deliveryHourRefs.current = deliveryPlaces.map(
      (_, i) => deliveryHourRefs.current[i] || null
    );
  }, [deliveryPlaces]);

  useEffect(() => {
    const fetchLoadBearerDetails = async () => {
      try {
        setIsFetching(true);
        const response = await axios.get(`/loads/${loadId}/editloads`);
        const loadData = response.data;

        console.log("[EditLoadBearers] editloads load-level:", {
          length: loadData.length,
          width: loadData.width,
          height: loadData.height,
          weight: loadData.weight,
          merchandise: loadData.merchandise,
        });
        
        console.log("[EditLoadBearers] editloads pickup dimensions:", {
          pickupLocations: loadData.pickupLocations?.map((p) => ({
            location: p.location,
            length: p.length,
            width: p.width,
            height: p.height,
          })),
          pickupPoints: loadData.pickupPoints?.map((p) => ({
            location: p.location,
            length: p.length,
            width: p.width,
            height: p.height,
          })),
        });
        
        console.log("[EditLoadBearers] editloads delivery dimensions:", {
          deliveryLocations: loadData.deliveryLocations?.map((p) => ({
            location: p.location,
            length: p.length,
            width: p.width,
            height: p.height,
          })),
          deliveryPoints: loadData.deliveryPoints?.map((p) => ({
            location: p.location,
            length: p.length,
            width: p.width,
            height: p.height,
          })),
        });

        console.log("📧 editloads clientContactEmails:", loadData.clientContactEmails);
        console.log("📧 editloads subcontractedEmails:", loadData.subcontractedEmails);

        if (loadData.clientType === "industry" && loadData.industryClientId) {
          setIndustryClientPrefill(
            makeOption(
              loadData.industryClientId,
              loadData.industryClientName || loadData.industryClient?.companyName,
              t("labels.clientFallback")
            )
          );
        } else if (
          loadData.clientType === "carrier" &&
          loadData.carrierClientId
        ) {
          setCarrierClientPrefill(
            makeOption(
              loadData.carrierClientId,
              loadData.carrierClientName || loadData.carrierClient?.companyName,
              t("labels.clientFallback")
            )
          );
        }

        // ✅ carrierId: external => id ; internal (null) => "__internal__"
        if (loadData.carrierId != null) {
          setCarrierPrefill(
            makeOption(loadData.carrierId, loadData.carrier?.companyName, t("labels.carrier"))
          );
          setCarrierId(toId(loadData.carrierId));
        } else {
          setCarrierPrefill(null);
          setCarrierId("__internal__");
        }

        const fromPickups = (loadData.pickupLocations || [])
          .map((p) => ({
            value: String(p.placeId ?? p.location ?? ""),
            label: p.location || "",
            fullData: p.place || null,
          }))
          .filter((o) => o.value && o.label);

        const fromDeliveries = (loadData.deliveryLocations || [])
          .map((p) => ({
            value: String(p.placeId ?? p.location ?? ""),
            label: p.location || "",
            fullData: p.place || null,
          }))
          .filter((o) => o.value && o.label);

        setPrefillPlaceOptions(
          uniqBy([...fromPickups, ...fromDeliveries], (o) => String(o.value))
        );
        setPlaceOptions((prev) =>
          prev.length
            ? prev
            : uniqBy([...fromPickups, ...fromDeliveries], (o) => String(o.value))
        );

        if (!loadData.loadBearer) {
          setLengthError(t("errors.notLoadBearer"));
          return;
        }

        if (loadData.clientType === "industry" && loadData.industryClientId) {
          setFetchedIndustryClientId(loadData.industryClientId);
          setIndustryClientId(loadData.industryClientId);
          setCarrierClientId("");
          setClientType("industry");
        
          setSelectedClient({
            label:
              loadData.industryClientName || t("labels.industryClientFallback"),
            value: loadData.industryClientId,
          });
        } else if (
          loadData.clientType === "carrier" &&
          loadData.carrierClientId
        ) {
          setFetchedCarrierClientId(loadData.carrierClientId);
          setCarrierClientId(loadData.carrierClientId);
          setIndustryClientId("");
          setClientType("carrier");
        
          setSelectedClient({
            label: loadData.carrierClientName || t("labels.carrierClientFallback"),
            value: loadData.carrierClientId,
          });
        }

        setLoad(loadData);
        setLength(loadData.length || "");
        setHeight(loadData.height || "");
        setWidth(loadData.width || "");
        setWeight(loadData.weight ?? "");
        setMerchandise(loadData.merchandise ?? "");
        setPrice(loadData.price ?? "");
        setPriceCurrency(loadData.priceCurrency || "EUR");
        // 🆕 Trailer Type (load-level)
        setTrailerType(
          loadData.trailerType != null ? String(loadData.trailerType).trim().toUpperCase() : null
        );
        setTrailerTypeOther(loadData.trailerTypeOther ?? "");

        setPrice4subcontracted(
          loadData.price4subcontracted ?? loadData.price4subcontrated ?? ""
        );

        setNotes4driver(loadData.notes4driver ?? "");
        setNotes4subcontracted(loadData.notes4subcontracted ?? "");
                setSelectedClientContactEmails(() => {
          const raw = loadData.clientContactEmails;
          if (!raw) return [];

          if (Array.isArray(raw)) {
            return raw
              .map((email) => String(email || "").trim().toLowerCase())
              .filter((email) => isValidEmail(email))
              .map((email) => ({ value: email, label: email }));
          }

          if (typeof raw === "string") {
            return raw
              .split(",")
              .map((email) => String(email || "").trim().toLowerCase())
              .filter((email) => isValidEmail(email))
              .map((email) => ({ value: email, label: email }));
          }

          return [];
        });

        setSelectedSubcontractedEmails(() => {
          const raw = loadData.subcontractedEmails;
          if (!raw) return [];

          if (Array.isArray(raw)) {
            return raw
              .map((email) => String(email || "").trim().toLowerCase())
              .filter((email) => isValidEmail(email))
              .map((email) => ({ value: email, label: email }));
          }

          if (typeof raw === "string") {
            return raw
              .split(",")
              .map((email) => String(email || "").trim().toLowerCase())
              .filter((email) => isValidEmail(email))
              .map((email) => ({ value: email, label: email }));
          }

          return [];
        });

        setPickUpPlaces(
          Array.isArray(loadData.pickupLocations)
            ? loadData.pickupLocations.map((place) => ({
                location: place.location || "",
                date: formatInputDate(place.date || ""),
                hour: place.hour || "",
        
                // 🆕 point-level dimensions
                length: place.length ?? "",
                width: place.width ?? "",
                height: place.height ?? "",
        
                placeId: place.placeId || null,
        
                address: place.address || "",
                postalCode: place.postalCode || "",
                city: place.city || "",
                country: place.country || "",
                gMapsLink: place.gMapsLink || "",
                coordinates: place.coordinates || "",
                contactPhone: place.contactPhone || "",

                // 🆕 reference (optional)
                reference: place.reference || "",

                // 🆕 loadingAccess
                loadingAccess: place.loadingAccess != null
                  ? String(place.loadingAccess).trim().toUpperCase()
                  : null,

                // 🆕 handlingEquipment
                handlingEquipment: place.handlingEquipment != null
                  ? String(place.handlingEquipment).trim().toUpperCase()
                  : null,
                handlingEquipmentOther: place.handlingEquipmentOther || "",

                pointLengthError: false,
                pointWidthError: false,
                pointHeightError: false,

                showMoreFields: false,
              }))
            : []
        );

        setDeliveryPlaces(
          Array.isArray(loadData.deliveryLocations)
            ? loadData.deliveryLocations.map((place) => ({
                location: place.location || "",
                date: formatInputDate(place.date || ""),
                hour: place.hour || "",
        
                // 🆕 point-level dimensions
                length: place.length ?? "",
                width: place.width ?? "",
                height: place.height ?? "",
        
                placeId: place.placeId || null,
        
                address: place.address || "",
                postalCode: place.postalCode || "",
                city: place.city || "",
                country: place.country || "",
                gMapsLink: place.gMapsLink || "",
                coordinates: place.coordinates || "",
                contactPhone: place.contactPhone || "",

                // 🆕 reference (optional)
                reference: place.reference || "",

                // 🆕 loadingAccess
                loadingAccess: place.loadingAccess != null
                  ? String(place.loadingAccess).trim().toUpperCase()
                  : null,

                // 🆕 handlingEquipment
                handlingEquipment: place.handlingEquipment != null
                  ? String(place.handlingEquipment).trim().toUpperCase()
                  : null,
                handlingEquipmentOther: place.handlingEquipmentOther || "",

                pointLengthError: false,
                pointWidthError: false,
                pointHeightError: false,

                showMoreFields: false,
              }))
            : []
        );

        const sessionResponse = await axios.get(
          `/loads?transportsessionId=${loadData.transportsessionId}`
        );
        const sessionLoads = sessionResponse.data;

        const totalNormalLoadLength = sessionLoads
          .filter((load) => !load.loadBearer && !load.superImposable)
          .reduce((acc, load) => acc + parseFloat(load.length || 0), 0);

        const totalLoadBearersLength = sessionLoads
          .filter((load) => load.loadBearer && load.id !== loadData.id)
          .reduce((acc, load) => acc + parseFloat(load.length || 0), 0);

        const superImposableLoads = sessionLoads.filter(
          (load) => load.associatedLoadBearerId === loadData.id
        );

        const totalSuperImposableLen = superImposableLoads.reduce(
          (acc, load) => acc + parseFloat(load.length || 0),
          0
        );

        setTotalSuperImposableLength(totalSuperImposableLen);

        const availableLengthForLoadBearer = Math.max(
          0,
          MAX_LENGTH - totalNormalLoadLength - totalLoadBearersLength
        );
        setMaxAllowedLoadBearer(
          Math.max(availableLengthForLoadBearer, totalSuperImposableLen)
        );

        const tallestSuperImposableHeight = Math.max(
          ...superImposableLoads.map((load) => parseFloat(load.height) || 0),
          0
        );

        const availableHeightForLoadBearer = Math.max(
          0,
          MAX_HEIGHT - tallestSuperImposableHeight
        );
        setMaxAllowedHeight(availableHeightForLoadBearer);
      } catch (error) {
        console.error("Error fetching load bearer details:", error);
        setLengthError(t("errors.failedFetch"));
      } finally {
        setIsFetching(false);
        setReady(true);
      }
    };

    fetchLoadBearerDetails();
  }, [loadId, t]);

  useEffect(() => {
    if (!load?.transportsessionId) return;

    const fetchExistingPoints = async () => {
      try {
        const res = await axios.get(`/pickupdeliveries/${load.transportsessionId}`);
        setExistingDateHourConflicts(res.data);
      } catch (err) {
        console.error("❌ Error fetching pickup/delivery conflicts:", err);
      }
    };

    fetchExistingPoints();
  }, [load?.transportsessionId]);

  useEffect(() => {
    const allNewPoints = [...pickUpPlaces, ...deliveryPlaces];
    const conflictPairs = new Set();
    let conflictMessages = [];

    allNewPoints.forEach((newPoint) => {
      if (!newPoint.date || !newPoint.hour) return;

      const match = existingDateHourConflicts.find(
        (existing) =>
          existing.date === newPoint.date &&
          existing.hour === newPoint.hour &&
          existing.loadId !== load.id
      );
      if (match) {
        conflictPairs.add(`${newPoint.date}|${newPoint.hour}`);
        conflictMessages.push(
          t("errors.conflictWithExisting", {
            type: formatPointType(match.type),
            location: match.location,
            date: match.date,
            hour: match.hour,
          })
        );
      }
    });

    setConflictDateHours(conflictPairs);
    setConflictError(conflictMessages.join("\n"));
  }, [pickUpPlaces, deliveryPlaces, existingDateHourConflicts, load?.id, t]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const [industriesRes, carriersRes] = await Promise.all([
          axios.get("/industries"),
          axios.get("/carriers"),
        ]);
        setIndustryClients(industriesRes.data);
        setCarrierClients(carriersRes.data);
        setCarriers(carriersRes.data);

        setIndustryOptions(
          industriesRes.data.map((c) => ({
            label: c.companyName,
            value: c.id,
          }))
        );

        setCarrierOptions(
          carriersRes.data.map((c) => ({
            label: c.companyName,
            value: c.id,
          }))
        );

        if (fetchedIndustryClientId) setIndustryClientId(fetchedIndustryClientId);
        if (fetchedCarrierClientId) setCarrierClientId(fetchedCarrierClientId);
      } catch (err) {
        console.error("❌ Error fetching clients:", err);
      }
    };

    fetchClients();
  }, [fetchedIndustryClientId, fetchedCarrierClientId]);

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const { data } = await axios.get("/places");
        const fetched = Array.isArray(data) ? data : [];

        const bestByKey = new Map();
        for (const p of fetched) {
          const k = keyOfPlace(p);
          const s = scoreOfPlace(p);
          const prev = bestByKey.get(k);
          const isBetter =
            !prev ||
            s > prev.score ||
            new Date(p.updatedAt || 0) > new Date(prev.updatedAt || 0);
          if (isBetter) bestByKey.set(k, { p, score: s, updatedAt: p.updatedAt });
        }

        const seeded = (prefillPlaceOptions || []).map((o) => o.fullData).filter(Boolean);
        for (const p of seeded) {
          const k = keyOfPlace(p);
          if (!bestByKey.has(k))
            bestByKey.set(k, { p, score: scoreOfPlace(p), updatedAt: p.updatedAt });
        }

        const dedupedOptions = Array.from(bestByKey.values())
          .map(({ p }) => ({
            value: String(p.id),
            label: p.placeName,
            metaCity: p.city || "",
            metaCountry: p.country || "",
            fullData: p,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

        const extras = (prefillPlaceOptions || [])
          .filter((o) => o.fullData && !bestByKey.has(keyOfPlace(o.fullData)))
          .map((o) => ({
            value: String(o.value),
            label: o.label,
            metaCity: o.fullData?.city || "",
            metaCountry: o.fullData?.country || "",
            fullData: o.fullData,
          }));

        setPlaceOptions(uniqBy([...extras, ...dedupedOptions], (o) => String(o.value)));
      } catch (err) {
        console.error("❌ Error fetching places:", err);
        setPlaceOptions((prev) =>
          prev.length
            ? prev
            : (prefillPlaceOptions || [])
                .filter((o) => o.fullData)
                .map((o) => ({ ...o, value: String(o.value) }))
        );
      }
    };

    fetchPlaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasSubmitted) return;

    const missingLocation = pickUpPlaces.some(
      (p) => !p.location?.trim?.()
    );

    setPickUpFieldError(
      missingLocation ? t("errors.locationRequired") : ""
    );

    const invalidIdx = [];
    pickUpPlaces.forEach((p, i) => {
      if (!p.location?.trim?.()) invalidIdx.push(i);
    });

    setInvalidFields((prev) => ({
      ...prev,
      pickups: invalidIdx,
    }));
  }, [pickUpPlaces, hasSubmitted, t]);

  useEffect(() => {
    if (!hasSubmitted) return;

    const missingLocation = deliveryPlaces.some(
      (p) => !p.location?.trim?.()
    );

    setDeliveryFieldError(
      missingLocation ? t("errors.locationRequired") : ""
    );

    const invalidIdx = [];
    deliveryPlaces.forEach((p, i) => {
      if (!p.location?.trim?.()) invalidIdx.push(i);
    });

    setInvalidFields((prev) => ({
      ...prev,
      deliveries: invalidIdx,
    }));
  }, [deliveryPlaces, hasSubmitted, t]);

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);
  
        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };
  
  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickUpPlaces);
  }, [pickUpPlaces]);
  
  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPlaces);
  }, [deliveryPlaces]);
  
  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);
  
    const lengthMatches =
      Math.round(pickupLength * 100) === Math.round(deliveryLength * 100);
  
    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches,
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);
  
  const hasAnyPointDimension = useMemo(() => {
    return [...pickUpPlaces, ...deliveryPlaces].some((point) => {
      return (
        String(point.length || "").trim() ||
        String(point.width || "").trim() ||
        String(point.height || "").trim()
      );
    });
  }, [pickUpPlaces, deliveryPlaces]);
  
  useEffect(() => {
    if (!hasAnyPointDimension) return;
  
    const nextLength =
      pointDimensionSummary.length > 0 ? pointDimensionSummary.length.toFixed(2) : "";
  
    const nextWidth =
      pointDimensionSummary.width > 0 ? pointDimensionSummary.width.toFixed(2) : "";
  
    const nextHeight =
      pointDimensionSummary.height > 0 ? pointDimensionSummary.height.toFixed(2) : "";
  
    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);
  
    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    hasAnyPointDimension,
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  const validateHourFormat = (value) => {
    if (!value) return true;
    const [hh, mm] = value.split(":").map(Number);
    return (
      /^\d{2}:\d{2}$/.test(value) &&
      !isNaN(hh) &&
      !isNaN(mm) &&
      hh >= 0 &&
      hh <= 23 &&
      mm >= 0 &&
      mm <= 59
    );
  };

  const handleLengthChange = (e) => {
    let value = e.target.value;
    setLength(value);
    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || numericValue < MIN_LENGTH_NORMAL) {
      setLengthError(
        t("errors.lengthMin", { min: MIN_LENGTH_NORMAL.toFixed(2) })
      );
    } else if (numericValue < totalSuperImposableLength) {
      setLengthError(
        t("errors.lengthMinSuperImposable", {
          min: totalSuperImposableLength.toFixed(2),
        })
      );
    } else if (numericValue > maxAllowedLoadBearer) {
      setLengthError(
        t("errors.lengthMax", { max: maxAllowedLoadBearer.toFixed(2) })
      );
    } else {
      setLengthError("");
    }
  };

  const handleHeightChange = (e) => {
    let value = e.target.value;
    setHeight(value);
    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || numericValue < 0.01) {
      setHeightError(t("errors.heightMin", { min: "0.01" }));
    } else if (numericValue > maxAllowedHeight) {
      setHeightError(t("errors.heightMax", { max: maxAllowedHeight.toFixed(2) }));
    } else {
      setHeightError("");
    }
  };

  const handleWidthChange = (e) => {
    let value = e.target.value;
    setWidth(value);
    const numericValue = parseFloat(value);

    if (isNaN(numericValue) || numericValue < 0.01) {
      setWidthError(t("errors.widthMin", { min: "0.01" }));
    } else if (numericValue > MAX_WIDTH) {
      setWidthError(t("errors.widthMax", { max: MAX_WIDTH.toFixed(2) }));
    } else {
      setWidthError("");
    }
  };

  const handleWeightChange = (e) => {
    const v = e.target.value;
    setWeight(v);

    // deixa o user apagar sem levar logo com erro
    if (v === "" || v === null) {
      setWeightError("");
      return;
    }

    const n = Number(v);
    if (Number.isNaN(n) || n < 0) {
      // como não tens "weightInvalid" no i18n, usamos required como fallback
      setWeightError(t("errors.weightRequired"));
    } else {
      setWeightError("");
    }
  };

  const validateDeliveryAfterPickups = (deliveries = deliveryPlaces, pickups = pickUpPlaces) => {
    const pickupDateTimes = pickups
      .filter((p) => p.date && p.hour)
      .map((p) => new Date(`${p.date}T${p.hour}`));

    const errorIndexes = { date: [], hour: [] };

    deliveries.forEach((d, index) => {
      if (d.date && d.hour) {
        const deliveryDateTime = new Date(`${d.date}T${d.hour}`);
        for (const pickupDate of pickupDateTimes) {
          if (deliveryDateTime < pickupDate) {
            const deliveryDateOnly = d.date;
            const deliveryHour = d.hour;
            const pickupDateOnly = pickupDate.toISOString().split("T")[0];
            const pickupHour = pickupDate.toISOString().split("T")[1].slice(0, 5);

            if (deliveryDateOnly < pickupDateOnly) {
              errorIndexes.date.push(index);
            } else if (deliveryDateOnly === pickupDateOnly && deliveryHour < pickupHour) {
              errorIndexes.hour.push(index);
            }
          }
        }
      }
    });

    return errorIndexes;
  };

  const handlePriceChange = (e) => {
    const v = e.target.value;
    setPrice(v);

    if (v === "" || v === null) {
      setPriceError("");
      return;
    }

    const n = Number(v);
    if (Number.isNaN(n) || n < 0) {
      setPriceError(t("errors.priceNonNegative"));
    } else {
      setPriceError("");
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setHasSubmitted(true);

    setPickUpFieldError("");
    setDeliveryFieldError("");
    setTransportCarrierError("");
    setConflictError("");
    setInvalidFields({ pickups: [], deliveries: [] });

    setNotes4driverError("");
    setNotes4subcontractedError("");

    setPrice4subcontractedError("");
    setMerchandiseError("");
    setWeightError("");
    setTrailerTypeError("");
    setTrailerTypeOtherError("");

    let nextGeneralErrors = { clientType: "", client: "" };
    let hasBlockingError = false;

    const priceValue =
      String(price ?? "").trim() === ""
        ? null
        : Number(price);

    if (priceValue === null) {
      setPriceError(
        t("errors.priceRequired", {
          defaultValue: "❌ Price is required.",
        })
      );
      hasBlockingError = true;
    } else if (!Number.isFinite(priceValue) || priceValue < 0) {
      setPriceError(
        t("errors.priceNonNegative", {
          defaultValue: "❌ Price must be a valid non-negative number.",
        })
      );
      hasBlockingError = true;
    } else {
      setPriceError("");
    }

    // 🆕 Merchandise required
    if (!String(merchandise || "").trim()) {
      setMerchandiseError(t("errors.merchandiseRequired"));
      hasBlockingError = true;
    } else {
      setMerchandiseError("");
    }

    // 🆕 Weight optional, but validated when provided
    if (String(weight ?? "").trim() !== "") {
      const n = Number(weight);

      if (Number.isNaN(n) || n < 0) {
        setWeightError(t("errors.enterValidNumber"));
        hasBlockingError = true;
      } else {
        setWeightError("");
      }
    }

    // 🆕 Trailer Type optional
    setTrailerTypeError("");

    // 🆕 Trailer Type OTHER -> requires trailerTypeOther
    if (String(trailerType || "").toUpperCase() === "OTHER") {
      if (!String(trailerTypeOther || "").trim()) {
        setTrailerTypeOtherError(
          t("errors.trailerTypeOtherRequired", {
            defaultValue: "Please specify the trailer type.",
          })
        );
        hasBlockingError = true;
      } else {
        setTrailerTypeOtherError("");
      }
    } else {
      // se não for OTHER, garante null/empty no texto
      setTrailerTypeOtherError("");
    }

    if (conflictDateHours.size > 0) {
      setConflictError(t("errors.conflictDateHour"));
      hasBlockingError = true;
    }

    const resolvedClientId =
      clientType === "industry"
        ? industryClientId || selectedClient?.value
        : clientType === "carrier"
          ? carrierClientId || selectedClient?.value
          : null;

    nextGeneralErrors = {
      clientType: clientType
        ? ""
        : t("errors.clientTypeRequired", {
            defaultValue: "❌ Client type is required.",
          }),
      client: resolvedClientId
        ? ""
        : t("errors.clientRequired", {
            defaultValue: "❌ Client is required.",
          }),
    };

    if (nextGeneralErrors.clientType || nextGeneralErrors.client) {
      hasBlockingError = true;
    }

    setTransportCarrierError("");

    // Notes max length guards
    if (notes4driver && notes4driver.length > 2000) {
      setNotes4driverError(t("errors.notesMax"));
      return;
    }

    if (showNotes4subcontracted && notes4subcontracted && notes4subcontracted.length > 2000) {
      setNotes4subcontractedError(t("errors.notesMax"));
      return;
    }    

    setGeneralErrors(nextGeneralErrors);

    const pickupMissing = pickUpPlaces.length === 0;
    const deliveryMissing = deliveryPlaces.length === 0;

    if (pickupMissing) {
      setPickUpFieldError(t("errors.atLeastOnePickup"));
      hasBlockingError = true;
    }
    if (deliveryMissing) {
      setDeliveryFieldError(t("errors.atLeastOneDelivery"));
      hasBlockingError = true;
    }

    const invalidPickups = [];
    const invalidDeliveries = [];

    const nextPickups = pickUpPlaces.map((place) => ({ ...place }));
    const nextDeliveries = deliveryPlaces.map((place) => ({ ...place }));

    nextPickups.forEach((place, i) => {
      const hasLocationError = !place.location?.trim?.();
      const hasDateError = false;
      const hasHourError =
        String(place.hour || "").trim() !== "" &&
        !validateHourFormat(place.hour);

      const hasAddressError = false;
      const hasPostalCodeError = false;
      const hasCityError = false;
      const hasCountryError = false;
      const hasGMapsLinkError = false;
      const hasCoordinatesError = false;
      const hasContactPhoneError = false;

      // 🆕 point-level dimension validation
      const pointLength = Number(place.length);
      const pointWidth = Number(place.width);
      const pointHeight = Number(place.height);
      
      const hasPointLengthError =
        String(place.length ?? "").trim() !== "" &&
        (
          !Number.isFinite(pointLength) ||
          pointLength <= 0 ||
          pointLength > maxAllowedLoadBearer
        );
      
      const hasPointWidthError =
        String(place.width ?? "").trim() !== "" &&
        (
          !Number.isFinite(pointWidth) ||
          pointWidth <= 0 ||
          pointWidth > MAX_WIDTH
        );
      
      const hasPointHeightError =
        String(place.height ?? "").trim() !== "" &&
        (
          !Number.isFinite(pointHeight) ||
          pointHeight <= 0 ||
          pointHeight > maxAllowedHeight
        );
      
      // 🆕 loadingAccess required
      const hasLoadingAccessError = !place.loadingAccess;
      
      // 🆕 handlingEquipmentOther required only if OTHER
      const isOtherEquip = String(place.handlingEquipment || "").toUpperCase() === "OTHER";
      const hasHandlingEquipmentOtherError = isOtherEquip && !place.handlingEquipmentOther?.trim?.();
      
      place.handlingEquipmentOtherError = hasHandlingEquipmentOtherError;
      
      const anyError =
        hasLocationError ||
        hasDateError ||
        hasHourError ||
        hasPointLengthError ||
        hasPointWidthError ||
        hasPointHeightError ||
        hasAddressError ||
        hasPostalCodeError ||
        hasCityError ||
        hasCountryError ||
        hasGMapsLinkError ||
        hasCoordinatesError ||
        hasContactPhoneError ||
        hasLoadingAccessError ||
        hasHandlingEquipmentOtherError;

      place.loadingAccessError = hasLoadingAccessError;

      if (anyError) invalidPickups.push(i);
      place.locationError = hasLocationError;
      place.dateError = hasDateError;
      place.hourError = hasHourError;

      place.pointLengthError = hasPointLengthError;
      place.pointWidthError = hasPointWidthError;
      place.pointHeightError = hasPointHeightError;

      place.addressError = hasAddressError;
      place.postalCodeError = hasPostalCodeError;
      place.cityError = hasCityError;
      place.countryError = hasCountryError;
      place.gMapsLinkError = hasGMapsLinkError;
      place.coordinatesError = hasCoordinatesError;
      place.contactPhoneError = hasContactPhoneError;
      place.showMoreFields = anyError;
    });

    nextDeliveries.forEach((place, i) => {
      const hasLocationError = !place.location?.trim?.();
      const hasDateError = false;
      const hasHourError =
        String(place.hour || "").trim() !== "" &&
        !validateHourFormat(place.hour);

      const hasAddressError = false;
      const hasPostalCodeError = false;
      const hasCityError = false;
      const hasCountryError = false;
      const hasGMapsLinkError = false;
      const hasCoordinatesError = false;
      const hasContactPhoneError = false;

      // 🆕 point-level dimension validation
      const pointLength = Number(place.length);
      const pointWidth = Number(place.width);
      const pointHeight = Number(place.height);
      
      const hasPointLengthError =
        String(place.length ?? "").trim() !== "" &&
        (
          !Number.isFinite(pointLength) ||
          pointLength <= 0 ||
          pointLength > maxAllowedLoadBearer
        );
      
      const hasPointWidthError =
        String(place.width ?? "").trim() !== "" &&
        (
          !Number.isFinite(pointWidth) ||
          pointWidth <= 0 ||
          pointWidth > MAX_WIDTH
        );
      
      const hasPointHeightError =
        String(place.height ?? "").trim() !== "" &&
        (
          !Number.isFinite(pointHeight) ||
          pointHeight <= 0 ||
          pointHeight > maxAllowedHeight
        );
      
      const hasLoadingAccessError = false;
      const isOtherEquip = String(place.handlingEquipment || "").toUpperCase() === "OTHER";
      const hasHandlingEquipmentOtherError = isOtherEquip && !place.handlingEquipmentOther?.trim?.();
      
      place.handlingEquipmentOtherError = hasHandlingEquipmentOtherError;
      
      const anyError =
        hasLocationError ||
        hasDateError ||
        hasHourError ||
        hasPointLengthError ||
        hasPointWidthError ||
        hasPointHeightError ||
        hasAddressError ||
        hasPostalCodeError ||
        hasCityError ||
        hasCountryError ||
        hasGMapsLinkError ||
        hasCoordinatesError ||
        hasContactPhoneError ||
        hasLoadingAccessError ||
        hasHandlingEquipmentOtherError;

      place.loadingAccessError = hasLoadingAccessError;

      if (anyError) invalidDeliveries.push(i);
      place.locationError = hasLocationError;
      place.dateError = hasDateError;
      place.hourError = hasHourError;

      place.pointLengthError = hasPointLengthError;
      place.pointWidthError = hasPointWidthError;
      place.pointHeightError = hasPointHeightError;

      place.addressError = hasAddressError;
      place.postalCodeError = hasPostalCodeError;
      place.cityError = hasCityError;
      place.countryError = hasCountryError;
      place.gMapsLinkError = hasGMapsLinkError;
      place.coordinatesError = hasCoordinatesError;
      place.contactPhoneError = hasContactPhoneError;
      place.showMoreFields = anyError;
    });

    setPickUpPlaces(nextPickups);
    setDeliveryPlaces(nextDeliveries);

    if (invalidPickups.length > 0) {
      if (nextPickups.some((place) => place.locationError)) {
        setPickUpFieldError(t("errors.locationRequired"));
      }

      hasBlockingError = true;
    }

    if (invalidDeliveries.length > 0) {
      if (nextDeliveries.some((place) => place.locationError)) {
        setDeliveryFieldError(t("errors.locationRequired"));
      }

      hasBlockingError = true;
    }

    setInvalidFields({
      pickups: invalidPickups,
      deliveries: invalidDeliveries,
    });

    const toCents = (value) => Math.round(Number(value || 0) * 100);

    const pickupLengthTotal = nextPickups.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const deliveryLengthTotal = nextDeliveries.reduce((sum, point) => {
      return sum + Number(point.length || 0);
    }, 0);

    const loadLengthValue = Number(length);

    const allPickupLengthsProvided =
      nextPickups.length > 0 &&
      nextPickups.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    const allDeliveryLengthsProvided =
      nextDeliveries.length > 0 &&
      nextDeliveries.every(
        (point) => String(point.length ?? "").trim() !== ""
      );

    if (
      Number.isFinite(loadLengthValue) &&
      loadLengthValue > 0 &&
      allPickupLengthsProvided &&
      toCents(pickupLengthTotal) !== toCents(loadLengthValue)
    ) {
      hasBlockingError = true;

      setPickUpFieldError(
        t("errors.pickupLengthTotalMustMatchLoad", {
          defaultValue: "❌ Total Pick-Up length must equal the Load length.",
        })
      );

      nextPickups.forEach((p) => {
        p.pointLengthError = true;
      });
    }

    if (
      Number.isFinite(loadLengthValue) &&
      loadLengthValue > 0 &&
      allDeliveryLengthsProvided &&
      toCents(deliveryLengthTotal) !== toCents(loadLengthValue)
    ) {
      hasBlockingError = true;

      setDeliveryFieldError(
        t("errors.deliveryLengthTotalMustMatchLoad", {
          defaultValue: "❌ Total Delivery length must equal the Load length.",
        })
      );

      nextDeliveries.forEach((p) => {
        p.pointLengthError = true;
      });
    }

    setPickUpPlaces(nextPickups);
    setDeliveryPlaces(nextDeliveries);

    if (!pickupMissing && !deliveryMissing) {
      const invalidIndexes = validateDeliveryAfterPickups(nextDeliveries, nextPickups);
      const anyOrderError = invalidIndexes.date.length > 0 || invalidIndexes.hour.length > 0;
      if (anyOrderError) {
        setDeliveryPlaceError(t("errors.deliveryBeforePickupAny"));
        setInvalidDeliveryIndexes([
          ...new Set([...invalidIndexes.date, ...invalidIndexes.hour]),
        ]);
        hasBlockingError = true;
      } else {
        setDeliveryPlaceError("");
        setInvalidDeliveryIndexes([]);
      }
    }

    if (hasBlockingError) return;

    try {
      setLoading(true);

      const cleanNotes4driver =
        typeof notes4driver === "string" ? notes4driver.trim() || null : null;

      const cleanNotes4subcontracted =
        showNotes4subcontracted && typeof notes4subcontracted === "string"
          ? (notes4subcontracted.trim() || null)
          : null;

      const subcontractedPriceValue =
        showNotes4subcontracted
          ? (price4subcontracted === "" ? null : Number(price4subcontracted))
          : null;

      const cleanMerchandise = String(merchandise || "").trim() || null;

      const payload = {
        length:
          String(length ?? "").trim() === "" || !Number.isFinite(Number(length))
            ? null
            : Number(length),
        height:
          String(height ?? "").trim() === "" || !Number.isFinite(Number(height))
            ? null
            : Number(height),
        width:
          String(width ?? "").trim() === "" || !Number.isFinite(Number(width))
            ? null
            : Number(width),

        weight:
          String(weight ?? "").trim() === "" || !Number.isFinite(Number(weight))
            ? null
            : Number(weight),

        merchandise: cleanMerchandise,

        // 🆕 Trailer Type (load-level)
        trailerType: trailerType ?? null,
        trailerTypeOther:
          String(trailerType || "").toUpperCase() === "OTHER"
            ? (String(trailerTypeOther || "").trim() || null)
            : null,

        price: priceValue,
        priceCurrency: priceCurrency || "EUR",
        price4subcontracted: subcontractedPriceValue,
        // compat com typo antigo (remove mais tarde quando normalizares tudo)
        price4subcontrated: subcontractedPriceValue,
        notes4driver: cleanNotes4driver,
        notes4subcontracted: cleanNotes4subcontracted,
        subcontractedEmails: selectedSubcontractedEmails.map((opt) => opt.value),
        pickupLocations: nextPickups.map((p) => ({ ...p })),
        deliveryLocations: nextDeliveries.map((p) => ({ ...p })),
        pickupPoints: nextPickups.map(
          ({
            location,
            date,
            hour,
            length,
            width,
            height,
            address,
            postalCode,
            city,
            country,
            gMapsLink,
            coordinates,
            contactPhone,
            loadingAccess,
            handlingEquipment,
            handlingEquipmentOther,
        
            // 🆕
            reference,
          }) => ({
            location,
            date,
            hour: hour || null,
        
            // 🆕 point-level dimensions
            length: length === "" ? null : Number(length),
            width: width === "" ? null : Number(width),
            height: height === "" ? null : Number(height),
        
            address,
            postalCode,
            city,
            country,
            gMapsLink,
            coordinates,
            contactPhone,

            // 🆕 reference (optional)
            reference: String(reference || "").trim() || null,

            loadingAccess: loadingAccess ?? null,
            handlingEquipment: handlingEquipment ?? null,
            handlingEquipmentOther: handlingEquipmentOther?.trim?.() || null,
          })
        ),

        deliveryPoints: nextDeliveries.map(
          ({
            location,
            date,
            hour,
            length,
            width,
            height,
            address,
            postalCode,
            city,
            country,
            gMapsLink,
            coordinates,
            contactPhone,
            loadingAccess,
            handlingEquipment,
            handlingEquipmentOther,
        
            // 🆕
            reference,
          }) => ({
            location,
            date,
            hour: hour || null,
        
            // 🆕 point-level dimensions
            length: length === "" ? null : Number(length),
            width: width === "" ? null : Number(width),
            height: height === "" ? null : Number(height),
        
            address,
            postalCode,
            city,
            country,
            gMapsLink,
            coordinates,
            contactPhone,

            // 🆕 reference (optional)
            reference: String(reference || "").trim() || null,

            loadingAccess: loadingAccess ?? null,
            handlingEquipment: handlingEquipment ?? null,
            handlingEquipmentOther: handlingEquipmentOther?.trim?.() || null,
          })
        ),

        clientType: clientType || null,
        industryClientId:
          clientType === "industry"
            ? (selectedClient?.value || industryClientId || null)
            : null,
        carrierClientId:
          clientType === "carrier"
            ? (selectedClient?.value || carrierClientId || null)
            : null,
        clientContactEmails: selectedClientContactEmails.map((opt) => opt.value),
        carrierId:
          !carrierId || carrierId === "__internal__"
            ? null
            : Number(carrierId),
      };

      await axios.put(`/loads/${loadId}`, payload);
      setSuccessPopupOpen(true);
    } catch (error) {
      console.error("❌ Error updating load bearer:", error);
      setLengthError(t("errors.failedUpdate"));
    } finally {
      setLoading(false);
    }
  };

  const handlePrice4subcontractedChange = (e) => {
    const v = e.target.value;
    setPrice4subcontracted(v);

    if (v === "" || v === null) {
      setPrice4subcontractedError("");
      return;
    }

    const n = Number(v);
    if (Number.isNaN(n) || n < 0) {
      setPrice4subcontractedError(t("errors.priceNonNegative"));
    } else {
      setPrice4subcontractedError("");
    }
  };

  const addDeliveryPlace = () => {
    if (deliveryPlaces.length >= MAX_POINTS) return;
    setDeliveryPlaces([
      ...deliveryPlaces,
      {
        location: "",
        date: "",
        hour: "",
  
        // 🆕 point-level dimensions
        length: "",
        width: "",
        height: "",
  
        placeId: null,
  
        address: "",
        postalCode: "",
        city: "",
        country: "",
        gMapsLink: "",
        coordinates: "",
        contactPhone: "",

        // 🆕 reference (optional)
        reference: "",

        // 🆕 loadingAccess
        loadingAccess: null,
        loadingAccessError: false,

        // 🆕 handlingEquipment
        handlingEquipment: null,
        handlingEquipmentOther: "",
        handlingEquipmentError: false,
        handlingEquipmentOtherError: false,

        pointLengthError: false,
        pointWidthError: false,
        pointHeightError: false,

        showMoreFields: false,
      }
    ]);
  };

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) return t("errors.enterValidNumber");

    if (field === "length") {
      if (n > maxAllowedLoadBearer) {
        return t("errors.lengthMax", {
          max: maxAllowedLoadBearer.toFixed(2),
        });
      }

      return "";
    }

    if (field === "width") {
      if (n > MAX_WIDTH) {
        return t("errors.widthMax", {
          max: MAX_WIDTH.toFixed(2),
        });
      }

      return "";
    }

    if (field === "height") {
      if (n > maxAllowedHeight) {
        return t("errors.heightMax", {
          max: maxAllowedHeight.toFixed(2),
        });
      }

      return "";
    }

    return "";
  };

  const handlePickUpPlaceChange = (event, index, field) => {
    const rawVal = event.target.value;

    setPickUpPlaces((prev) => {
      const updated = [...prev];
      const place = { ...updated[index] };

      if (field === "hour") {
        const raw = rawVal.replace(/[^0-9]/g, "");
        let formatted = raw.length >= 3 ? `${raw.slice(0, 2)}:${raw.slice(2, 4)}` : raw;
        place.hour = formatted;
        if (!formatted) place.hourError = true;
        else {
          const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
          place.hourError = !regex.test(formatted);
        }
      } else {
        place[field] = rawVal;
        if (field === "location" && rawVal.trim()) place.locationError = false;
        if (field === "date" && rawVal) place.dateError = false;
      
        // 🆕 live point-level dimension validation
        if (["length", "width", "height"].includes(field)) {
          const dimensionError = getPointDimensionError(field, rawVal);

          if (field === "length") {
            place.pointLengthError = !!dimensionError;
            place.pointLengthErrorMessage = dimensionError;
          }

          if (field === "width") {
            place.pointWidthError = !!dimensionError;
            place.pointWidthErrorMessage = dimensionError;
          }

          if (field === "height") {
            place.pointHeightError = !!dimensionError;
            place.pointHeightErrorMessage = dimensionError;
          }
        }
      }

      updated[index] = place;
      return updated;
    });

    setPickUpFieldError((prev) => {
      const hasEmpty = [...pickUpPlaces].some((p) => !p.location?.trim?.() || !p.date || !p.hour);
      return hasEmpty ? prev : "";
    });

    const updatedErrors = validateDeliveryAfterPickups(deliveryPlaces, [
      ...pickUpPlaces.slice(0, index),
      { ...pickUpPlaces[index], [field]: rawVal },
      ...pickUpPlaces.slice(index + 1),
    ]);

    setInvalidDeliveryIndexes([...new Set([...updatedErrors.date, ...updatedErrors.hour])]);
    setDeliveryBeforePickupErrors(updatedErrors);

    const hasDateErr = updatedErrors.date.length > 0;
    const hasHourErr = updatedErrors.hour.length > 0;

    setDeliveryPlaceError(
      hasDateErr
        ? t("errors.deliveryDateBeforePickup")
        : hasHourErr
        ? t("errors.deliveryHourBeforePickup")
        : ""
    );

    if (field === "hour") {
      const isValid = validateHourFormat(rawVal);
      setGlobalHourFormatError(isValid ? "" : t("errors.invalidHourShort"));
    }
  };

  const handleDeliveryPlaceChange = (event, index, field) => {
    const rawVal = event.target.value;

    setDeliveryPlaces((prev) => {
      const updated = [...prev];
      const place = { ...updated[index] };

      if (field === "hour") {
        const raw = rawVal.replace(/[^0-9]/g, "");
        let formatted = raw.length >= 3 ? `${raw.slice(0, 2)}:${raw.slice(2, 4)}` : raw;
        place.hour = formatted;
        if (!formatted) place.hourError = true;
        else {
          const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
          place.hourError = !regex.test(formatted);
        }
      } else {
        place[field] = rawVal;
        if (field === "location" && rawVal.trim()) place.locationError = false;
        if (field === "date" && rawVal) place.dateError = false;
      
        // 🆕 live point-level dimension validation
        if (["length", "width", "height"].includes(field)) {
          const dimensionError = getPointDimensionError(field, rawVal);

          if (field === "length") {
            place.pointLengthError = !!dimensionError;
            place.pointLengthErrorMessage = dimensionError;
          }

          if (field === "width") {
            place.pointWidthError = !!dimensionError;
            place.pointWidthErrorMessage = dimensionError;
          }

          if (field === "height") {
            place.pointHeightError = !!dimensionError;
            place.pointHeightErrorMessage = dimensionError;
          }
        }
      }

      updated[index] = place;
      return updated;
    });

    setDeliveryFieldError((prev) => {
      const hasEmpty = [...deliveryPlaces].some((p) => !p.location?.trim?.() || !p.date || !p.hour);
      return hasEmpty ? prev : "";
    });

    const updatedErrors = validateDeliveryAfterPickups(
      [
        ...deliveryPlaces.slice(0, index),
        { ...deliveryPlaces[index], [field]: rawVal },
        ...deliveryPlaces.slice(index + 1),
      ],
      pickUpPlaces
    );

    setInvalidDeliveryIndexes([...new Set([...updatedErrors.date, ...updatedErrors.hour])]);
    setDeliveryBeforePickupErrors(updatedErrors);

    const hasDateErr = updatedErrors.date.length > 0;
    const hasHourErr = updatedErrors.hour.length > 0;

    setDeliveryPlaceError(
      hasDateErr
        ? t("errors.deliveryDateBeforePickup")
        : hasHourErr
        ? t("errors.deliveryHourBeforePickup")
        : ""
    );

    if (field === "hour") {
      const isValid = validateHourFormat(rawVal);
      setGlobalHourFormatError(isValid ? "" : t("errors.invalidHourShort"));
    }
  };

  const removeDeliveryPlace = (index) => {
    const updated = [...deliveryPlaces];
    updated.splice(index, 1);
    setDeliveryPlaces(updated);
  };

  const renderOption = (opt) => (
    <div style={{ lineHeight: 1.2 }}>
      <div>{opt.label}</div>
      {opt.metaCity ? (
        <div style={{ fontSize: 12, opacity: 0.75 }}>
          {opt.metaCity}
          {opt.metaCountry ? `, ${opt.metaCountry}` : ""}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="edit-load-bearer-container">
      <div className="edit-load-bearer-back-arrow" onClick={() => navigate("/loads")}>
        {t("actions.back")}
      </div>

      {successPopupOpen && (
        <div
          className="edit-load-bearer-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-load-bearer-success-title"
        >
          <div className="edit-load-bearer-popupModal">
            <div className="edit-load-bearer-popupIconWrap">
              <FaCheckCircle className="edit-load-bearer-popupIcon" />
            </div>

            <h2
              id="edit-load-bearer-success-title"
              className="edit-load-bearer-popupTitle"
            >
              {t("popup.updated_title")}
            </h2>

            <p className="edit-load-bearer-popupMessage">
              {t("popup.updated_message")}
            </p>

            <div className="edit-load-bearer-popupActions">
              <button
                type="button"
                className="edit-load-bearer-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="edit-load-bearer-title">{t("title")}</h1>

      <div className="edit-load-bearer-card">
        <form className="edit-load-bearer-form" onSubmit={handleSaveChanges}>
          {/* Line 1: Transport Session • Load • Audit */}
          <div className="edit-load-bearer-top-meta-row">
            <div className="edit-load-bearer-row elb-kv-row edit-load-bearer-top-left-meta">
              <span
                className="elb-kv-pair"
                title={
                  tsCounter != null
                    ? t("tooltips.transportSessionNumber", { n: tsCounter })
                    : load?.transportsessionId
                    ? t("tooltips.transportSessionId", { id: load.transportsessionId })
                    : ""
                }
              >
                <span className="elb-kv__key">{t("kv.transportSession")}:</span>
                <span className="elb-kv__value">{tsCounter ?? "—"}</span>
              </span>

              <span className="elb-kv__sep">•</span>

              <span
                className="elb-kv-pair"
                title={
                  loadCounter != null
                    ? t("tooltips.loadNumber", { n: loadCounter })
                    : load?.id
                    ? t("tooltips.loadId", { id: load.id })
                    : ""
                }
              >
                <span className="elb-kv__key">{t("kv.load")}:</span>
                <span className="elb-kv__value">{loadCounter ?? "—"}</span>
              </span>
            </div>

            <div className="edit-load-bearer-audit-inline">
              <span
                className="edit-load-bearer-audit-chip"
                title={displayAuditEmail(load?.createdByEmail ?? initialLoad?.createdByEmail)}
              >
                <span className="edit-load-bearer-audit-chip-label">
                  {t("labels.createdByEmail", { defaultValue: "Created by" })}
                </span>
                <span className="edit-load-bearer-audit-chip-value">
                  {displayAuditEmail(load?.createdByEmail ?? initialLoad?.createdByEmail)}
                </span>
              </span>

              <span
                className="edit-load-bearer-audit-chip"
                title={displayAuditEmail(load?.updatedByEmail ?? initialLoad?.updatedByEmail)}
              >
                <span className="edit-load-bearer-audit-chip-label">
                  {t("labels.updatedByEmail", { defaultValue: "Updated by" })}
                </span>
                <span className="edit-load-bearer-audit-chip-value">
                  {displayAuditEmail(load?.updatedByEmail ?? initialLoad?.updatedByEmail)}
                </span>
              </span>
            </div>
          </div>

          {/* Columns layout */}
          <div className="edit-load-bearer-columns">
            {/* Column 1 - Pick-Up Points */}
            <div className="edit-load-bearer-column">
              <h3 className="edit-load-bearer-column-title">{t("columns.loadingPoints")}</h3>

              {pickUpPlaces.map((place, index) => (
                <div key={index} className="edit-load-bearer-multi-location-input">
                  <div className="edit-load-bearer-label-row">
                    <label className="edit-load-bearer-points-label">
                      {t("fields.pickupPlace")}
                    </label>

                    {pickUpPlaces.length > 1 && (
                      <button
                        type="button"
                        className="edit-load-bearer-delete-btn"
                        onClick={() => {
                          const updated = [...pickUpPlaces];
                          updated.splice(index, 1);
                          setPickUpPlaces(updated);
                        }}
                        title={t("actions.removePickupPoint")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-load-bearer-input-row">
                    <div className="edit-load-bearer-select-wrapper">
                      <CreatableSelect
                        isClearable
                        isMulti={false}
                        placeholder={t("placeSelect.placeholder")}
                        value={
                          place.placeId
                            ? placeOptions.find((o) => String(o.value) === String(place.placeId)) || {
                                value: String(place.placeId),
                                label: place.location || t("labels.placeFallback"),
                              }
                            : place.location
                            ? { value: place.location, label: place.location }
                            : null
                        }
                        getOptionValue={(opt) => String(opt.value)}
                        formatOptionLabel={renderOption}
                        options={placeOptions.length ? placeOptions : prefillPlaceOptions}
                        onChange={(selectedOption) => {
                          const updated = [...pickUpPlaces];
                          if (selectedOption?.__isNew__) {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption.label,
                              locationError: false,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                            };
                          } else if (selectedOption?.fullData) {
                            const p = selectedOption.fullData;
                            updated[index] = {
                              ...updated[index],
                              placeId: String(p.id),
                              location: p.placeName,
                              address: p.address,
                              postalCode: p.postalCode,
                              city: p.city,
                              country: p.country,
                              gMapsLink: p.gMapsLink,
                              coordinates: p.coordinates,
                              contactPhone: p.contactPhone,
                              locationError: false,
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: updated[index].loadingAccess ?? null,
                              loadingAccessError: false,
                            };
                          } else {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption?.label || "",
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              locationError: false,
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                            };
                          }
                          setPickUpPlaces(updated);
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={
                          place.locationError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                        }
                      />
                    </div>
                  </div>

                  {place.locationError && (
                    <p className="edit-load-bearer-error">{t("errors.locationRequired")}</p>
                  )}

                  <div className="edit-load-bearer-datetime-group">
                    <div className="edit-load-bearer-custom-input-wrapper">
                      <input
                        type="date"
                        value={place.date || ""}
                        onChange={(e) => handlePickUpPlaceChange(e, index, "date")}
                        className={`edit-load-bearer-input ${
                          hasSubmitted &&
                          ((invalidFields.pickups.includes(index) && !place.date) ||
                            dateFieldErrors[`pickup-${index}`])
                            ? "input-error"
                            : ""
                        }`}
                        ref={(el) => {
                          if (el) dateRefs.current[index] = el;
                        }}
                      />
                      <FaCalendarAlt
                        className="edit-load-bearer-input-icon clickable"
                        onClick={(e) => {
                          e.preventDefault();
                          const input = dateRefs.current[index];
                          if (input) {
                            input.focus();
                            if (typeof input.showPicker === "function") input.showPicker();
                          }
                        }}
                      />
                    </div>

                    <div className="edit-load-bearer-custom-input-wrapper">
                      <input
                        type="text"
                        placeholder={t("placeSelect.hhmm")}
                        value={place.hour || ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          let formatted = raw;

                          if (raw.length >= 3) {
                            formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                          }

                          const updated = [...pickUpPlaces];
                          updated[index].hour = formatted;

                          if (!formatted) {
                            updated[index].hourError = true;
                          } else {
                            const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                            updated[index].hourError = !regex.test(formatted);
                          }

                          setPickUpPlaces(updated);
                          setGlobalHourFormatError("");
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                          const updated = [...pickUpPlaces];
                          updated[index].hourError = !regex.test(value);
                          setPickUpPlaces(updated);

                          if (!regex.test(value)) {
                            setGlobalHourFormatError(t("errors.invalidHourLong"));
                          } else {
                            setGlobalHourFormatError("");
                          }
                        }}
                        className={`edit-load-bearer-input ${place.hourError ? "input-error" : ""}`}
                      />
                      <FaClock className="edit-load-bearer-input-icon clickable" />
                    </div>
                  </div>

                  {place.dateError && (
                    <p className="edit-load-bearer-error">{t("errors.dateRequired")}</p>
                  )}

                  <div className="edit-load-bearer-point-dimensions-block">
                    <label className="edit-load-bearer-label edit-load-bearer-point-dimensions-title">
                      {t("fields.pointDimensions", {
                        defaultValue: "Dimensions of this point",
                      })}
                    </label>

                    <div className="edit-load-bearer-point-dimensions-row">
                      <div className="edit-load-bearer-point-dimension-field">
                        <label>{t("fields.pointLength", { defaultValue: "Length (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.length || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "length")}
                          className={`edit-load-bearer-input ${place.pointLengthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointLengthErrorMessage && (
                          <p className="edit-load-bearer-error">{place.pointLengthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-bearer-point-dimension-field">
                        <label>{t("fields.pointWidth", { defaultValue: "Width (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.width || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "width")}
                          className={`edit-load-bearer-input ${place.pointWidthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointWidthErrorMessage && (
                          <p className="edit-load-bearer-error">{place.pointWidthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-bearer-point-dimension-field">
                        <label>{t("fields.pointHeight", { defaultValue: "Height (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.height || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "height")}
                          className={`edit-load-bearer-input ${place.pointHeightError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointHeightErrorMessage && (
                          <p className="edit-load-bearer-error">{place.pointHeightErrorMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {hasSubmitted && (!place.hour || place.hour.trim() === "") && !place.hourError && (
                    <p className="edit-load-bearer-error">{t("errors.hourRequired")}</p>
                  )}
                  {place.hour && place.hourError && (
                    <p className="edit-load-bearer-error">{t("errors.invalidHourLong")}</p>
                  )}

                  {/* ✅ Loading Access (PICKUP) */}
                  <label className="edit-load-bearer-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-load-bearer-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        place.loadingAccessError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {place.loadingAccessError && (
                    <p className="edit-load-bearer-error">{t("errors.loadingAccessRequired")}</p>
                  )}

                  {/* ✅ Handling Equipment (PICKUP) */}
                  <label className="edit-load-bearer-label">
                    {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-load-bearer-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                      isClearable
                      placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        place.handlingEquipmentError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(place.handlingEquipment || "").toUpperCase() === "OTHER" && (
                    <div className="edit-load-bearer-field-group" style={{ marginBottom: 10 }}>
                      <label className="edit-load-bearer-label">
                        {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("fields.handlingEquipmentOtherPlaceholder", {
                          defaultValue: "Write the equipment...",
                        })}
                        value={place.handlingEquipmentOther || ""}
                        onChange={(e) => handleHandlingEquipmentOtherChange(index, e, true)}
                        className={`edit-load-bearer-input ${
                          place.handlingEquipmentOtherError ? "input-error" : ""
                        }`}
                      />
                    </div>
                  )}

                  {/* 🆕 Reference (PICKUP) - optional */}
                  <div className="edit-load-bearer-field-group" style={{ marginBottom: 10 }}>
                    <label className="edit-load-bearer-label">
                      {t("fields.reference", { defaultValue: "Reference" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("placeholders.reference", { defaultValue: "Optional reference..." })}
                      value={place.reference || ""}
                      onChange={(e) => handlePickUpPlaceChange(e, index, "reference")}
                      className="edit-load-bearer-input"
                    />
                  </div>

                  {place.showMoreFields ? (
                    <>
                      <div className="edit-load-bearer-pickup-extra-fields">
                        <input
                          type="text"
                          placeholder={t("placeFields.address")}
                          value={place.address || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "address")}
                          className={`edit-load-bearer-input ${place.addressError ? "input-error" : ""}`}
                        />

                        <div className="edit-load-bearer-row-compact">
                          <div className="edit-load-bearer-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeFields.postalCode")}
                              value={place.postalCode || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "postalCode")}
                              className={`edit-load-bearer-input ${place.postalCodeError ? "input-error" : ""}`}
                            />
                          </div>
                          <div className="edit-load-bearer-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeFields.city")}
                              value={place.city || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "city")}
                              className={`edit-load-bearer-input ${place.cityError ? "input-error" : ""}`}
                            />
                          </div>
                          <div className="edit-load-bearer-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeFields.country")}
                              value={place.country || ""}
                              onChange={(e) => handlePickUpPlaceChange(e, index, "country")}
                              className={`edit-load-bearer-input ${place.countryError ? "input-error" : ""}`}
                            />
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder={t("placeFields.contactPhone")}
                          value={place.contactPhone || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "contactPhone")}
                          className={`edit-load-bearer-input ${place.contactPhoneError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("placeFields.gMapsLink")}
                          value={place.gMapsLink || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "gMapsLink")}
                          className={`edit-load-bearer-input ${place.gMapsLinkError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("placeFields.coordinatesPlaceholder")}
                          value={place.coordinates || ""}
                          onChange={(e) => handlePickUpPlaceChange(e, index, "coordinates")}
                          className={`edit-load-bearer-input ${place.coordinatesError ? "input-error" : ""}`}
                        />
                      </div>

                      <div className="edit-load-bearer-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-load-bearer-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...pickUpPlaces];
                            updated[index].showMoreFields = false;
                            setPickUpPlaces(updated);
                          }}
                        >
                          {t("actions.hideFields")}{" "}
                          <FiChevronUp className="edit-load-bearer-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-load-bearer-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-load-bearer-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...pickUpPlaces];
                          updated[index].showMoreFields = true;
                          setPickUpPlaces(updated);
                        }}
                      >
                        {t("actions.moreFields")}{" "}
                        <FiChevronDown className="edit-load-bearer-toggle-arrow" />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {pickUpFieldError && (
                <p className="edit-load-bearer-error-pickup-error">{pickUpFieldError}</p>
              )}

              {pickUpPlaces.length < MAX_POINTS ? (
                <button
                  type="button"
                  className="edit-load-bearer-add-location-btn"
                  onClick={() =>
                    setPickUpPlaces([
                      ...pickUpPlaces,
                      {
                        location: "",
                        date: "",
                        hour: "",
                      
                        // 🆕 point-level dimensions
                        length: "",
                        width: "",
                        height: "",
                      
                        placeId: null,
                      
                        address: "",
                        postalCode: "",
                        city: "",
                        country: "",
                        gMapsLink: "",
                        coordinates: "",
                        contactPhone: "",

                        // 🆕 reference (optional)
                        reference: "",

                        showMoreFields: false,

                        // 🆕 loadingAccess
                        loadingAccess: null,
                        loadingAccessError: false,

                        // 🆕 handlingEquipment
                        handlingEquipment: null,
                        handlingEquipmentOther: "",
                        handlingEquipmentError: false,
                        handlingEquipmentOtherError: false,
                      },
                    ])
                  }
                >
                  {t("actions.addAnotherPickup")}
                </button>
              ) : (
                <p className="edit-load-bearer-error-max-points">
                  {t("warnings.maxPickupPoints", { max: MAX_POINTS })}
                </p>
              )}

              {pickUpPlaceError && <p className="edit-load-bearer-error">{pickUpPlaceError}</p>}
            </div>

            {/* Column 2 - Merchandise */}
            <div className="edit-load-bearer-column">
              <h3 className="edit-load-bearer-column-title">{t("columns.merchandise")}</h3>

              <div className="edit-load-bearer-merchandise-inline-row">
                <div className="edit-load-bearer-field-group edit-load-bearer-weight-field">
                  <label className="edit-load-bearer-label">
                    {t("fields.weight", { defaultValue: "Weight (kg)" })}
                  </label>

                  <input
                    type="number"
                    value={weight}
                    onChange={handleWeightChange}
                    className={`edit-load-bearer-input ${weightError ? "input-error" : ""}`}
                    min="0"
                    step="0.01"
                    placeholder="0"
                  />

                  {weightError && <p className="edit-load-bearer-error">{weightError}</p>}
                </div>

                <div className="edit-load-bearer-field-group edit-load-bearer-merchandise-field">
                  <label className="edit-load-bearer-label">
                    {t("fields.merchandise", { defaultValue: "Merchandise*" })}
                  </label>

                  <input
                    type="text"
                    value={merchandise}
                    onChange={(e) => {
                      const v = e.target.value;
                      setMerchandise(v);
                      if (merchandiseError && v.trim()) setMerchandiseError("");
                    }}
                    placeholder={t("placeholders.merchandise", {
                      defaultValue: "e.g. Pallets, Food, Furniture...",
                    })}
                    className={`edit-load-bearer-input edit-load-bearer-merchandise-input ${
                      merchandiseError ? "input-error" : ""
                    }`}
                  />

                  {merchandiseError && <p className="edit-load-bearer-error">{merchandiseError}</p>}
                </div>
              </div>

              <div className="edit-load-bearer-load-dimensions-summary">
                <label className="edit-load-bearer-label edit-load-bearer-load-dimensions-title">
                  {t("fields.loadDimensions", {
                    defaultValue: "Total load dimensions",
                  })}
                </label>

                <div className="edit-load-bearer-load-dimensions-row">
                  <div className="edit-load-bearer-load-dimension-field">
                    <label>{t("fields.length", { defaultValue: "Length (m)" })}</label>
                    <input
                      type="text"
                      value={length}
                      readOnly
                      className="edit-load-bearer-input edit-load-bearer-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-load-bearer-load-dimension-field">
                    <label>{t("fields.width", { defaultValue: "Width (m)" })}</label>
                    <input
                      type="text"
                      value={width}
                      readOnly
                      className="edit-load-bearer-input edit-load-bearer-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="edit-load-bearer-load-dimension-field">
                    <label>{t("fields.height", { defaultValue: "Height (m)" })}</label>
                    <input
                      type="text"
                      value={height}
                      readOnly
                      className="edit-load-bearer-input edit-load-bearer-auto-calculated-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {!pointDimensionSummary.lengthMatches && (
                  <p className="edit-load-bearer-dimension-warning-text">
                    {t("errors.pickupDeliveryLengthMismatch", {
                      defaultValue:
                        "⚠️ The sum of pickup and delivery lengths still does not match.",
                    })}
                  </p>
                )}

                <div className="edit-load-bearer-dimension-helper-text">
                  {t("fields.loadDimensionsHelper", {
                    defaultValue:
                      "Length is calculated from the sum of the points. Width and height use the highest registered value.",
                  })}
                </div>
              </div>

              {/* 🆕 Trailer Type (load-level) */}
              <div className="edit-load-bearer-field-group">
                <label className="edit-load-bearer-label">
                  {t("fields.trailerType", { defaultValue: "Trailer type" })}
                </label>

                <Select
                  options={trailerTypeOptions}
                  value={
                    trailerTypeOptions.find(
                      (o) => o.value === String(trailerType || "").trim().toUpperCase()
                    ) || null
                  }
                  onChange={handleTrailerTypeChange}
                  isClearable
                  isSearchable={false}
                  placeholder={t("placeholders.trailerType", { defaultValue: "Select trailer type..." })}
                  styles={{
                    ...customSelectStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  classNamePrefix={
                    trailerTypeError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                  }
                  menuPortalTarget={document.body}
                />

                {trailerTypeError && <p className="edit-load-bearer-error">{trailerTypeError}</p>}
              </div>

              {String(trailerType || "").toUpperCase() === "OTHER" && (
                <div className="edit-load-bearer-field-group elb-trailer-type-other">
                  <label className="edit-load-bearer-label">
                    {t("fields.trailerTypeOther", { defaultValue: "Other (specify)" })}
                  </label>

                  <input
                    type="text"
                    value={trailerTypeOther || ""}
                    onChange={handleTrailerTypeOtherChange}
                    placeholder={t("placeholders.trailerTypeOther", { defaultValue: "Write the trailer type..." })}
                    className={`edit-load-bearer-input ${trailerTypeOtherError ? "input-error" : ""}`}
                  />

                  {trailerTypeOtherError && (
                    <p className="edit-load-bearer-error">{trailerTypeOtherError}</p>
                  )}
                </div>
              )}

              <h3 className="edit-load-bearer-general-info-title">{t("sections.generalInformation")}</h3>

              <div className="edit-load-bearer-field-group">
                <label className="edit-load-bearer-label">{t("fields.price")}</label>

                <div className={`edit-load-bearer-price-wrapper ${priceError ? "input-error" : ""}`}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={t("placeholders.price")}
                    value={price}
                    onChange={handlePriceChange}
                    className="edit-load-bearer-input edit-load-bearer-price-input"
                  />

                  <div className="edit-load-bearer-price-currency">
                    <Select
                      options={CURRENCY_OPTIONS}
                      value={
                        CURRENCY_OPTIONS.find((opt) => opt.value === (priceCurrency || "EUR")) ||
                        CURRENCY_OPTIONS[0]
                      }
                      onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                      isSearchable={false}
                      isClearable={false}
                      styles={{
                        ...customSelectStyles,
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      classNamePrefix="edit-load-bearer-select"
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                {priceError && <p className="edit-load-bearer-error">{priceError}</p>}
              </div>

                            {/* 🆕 Price for Subcontracted (ONLY when carrierId !== companyId) */}
                            {showNotes4subcontracted && (
                <div className="edit-load-bearer-field-group">
                  <label className="edit-load-bearer-label">
                    {t("fields.price4subcontracted.label")}
                  </label>

                  <div
                    className={`edit-load-bearer-price-wrapper ${
                      price4subcontractedError ? "input-error" : ""
                    }`}
                  >
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t("fields.price4subcontracted.placeholder")}
                      value={price4subcontracted}
                      onChange={handlePrice4subcontractedChange}
                      className="edit-load-bearer-input edit-load-bearer-price-input"
                    />

                    {/* Reuse same currency selector */}
                    <div className="edit-load-bearer-price-currency">
                      <Select
                        options={CURRENCY_OPTIONS}
                        value={
                          CURRENCY_OPTIONS.find(
                            (opt) => opt.value === (priceCurrency || "EUR")
                          ) || CURRENCY_OPTIONS[0]
                        }
                        onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                        isSearchable={false}
                        isClearable={false}
                        styles={{
                          ...customSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        classNamePrefix="edit-load-bearer-select"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  {price4subcontractedError && (
                    <p className="edit-load-bearer-error">{price4subcontractedError}</p>
                  )}
                </div>
              )}

              <div className="edit-load-bearer-field-group">
                <label className="edit-load-bearer-label">{t("fields.clientType")}</label>
                <Select
                  value={
                    clientType
                      ? {
                          label: clientType === "industry" ? t("clientType.industry") : t("clientType.carrier"),
                          value: clientType,
                        }
                      : null
                  }
                  onChange={(selected) => {
                    const nextType = selected?.value || "";
                  
                    setClientType(nextType);
                    setSelectedClient(null);
                    setSelectedClientContactEmails([]);
                    setIndustryClientId("");
                    setCarrierClientId("");
                  
                    setGeneralErrors((prev) => ({ ...prev, clientType: "", client: "" }));
                  }}
                  options={[
                    { value: "industry", label: t("clientType.industry") },
                    { value: "carrier", label: t("clientType.carrier") },
                  ]}
                  styles={customSelectStyles}
                  isClearable
                  isSearchable={false}
                  classNamePrefix={
                    generalErrors.clientType ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                  }
                />
                {generalErrors.clientType && <p className="edit-load-bearer-error">{generalErrors.clientType}</p>}
              </div>

              {clientType && (
                <div className="edit-load-bearer-field-group">
                  <label className="edit-load-bearer-label">
                    {clientType === "industry" ? t("fields.industryClient") : t("fields.carrierClient")}
                  </label>
                  <Select
                    options={
                      clientType === "industry"
                        ? industryOptions.length === 0 && industryClientPrefill
                          ? [industryClientPrefill]
                          : industryOptions
                        : carrierOptions.length === 0 && carrierClientPrefill
                        ? [carrierClientPrefill]
                        : carrierOptions
                    }
                    value={selectedClient}
                    onChange={(selected) => {
                      setSelectedClient(selected || null);
                      setSelectedClientContactEmails([]);
                    
                      if (clientType === "industry") {
                        setIndustryClientId(selected ? selected.value : "");
                        setCarrierClientId("");
                      } else {
                        setCarrierClientId(selected ? selected.value : "");
                        setIndustryClientId("");
                      }
                    
                      setGeneralErrors((prev) => ({ ...prev, client: "" }));
                    }}
                    isClearable
                    styles={customSelectStyles}
                    classNamePrefix={generalErrors.client ? "edit-load-bearer-select-error" : "edit-load-bearer-select"}
                  />
                  {generalErrors.client && (
                    <p className="edit-load-bearer-error client-select-error">{generalErrors.client}</p>
                  )}

                  {selectedClient && (
                    <div className="edit-load-bearer-field-group">
                      <label className="edit-load-bearer-label">
                        {t("fields.clientContactEmails.label", {
                          defaultValue: "Client contact email(s)",
                        })}
                      </label>

                      <Select
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        options={selectedClientContactEmailOptions}
                        value={selectedClientContactEmails}
                        onChange={(selectedOptions) => {
                          const limited = Array.isArray(selectedOptions)
                            ? selectedOptions.slice(0, 3)
                            : [];
                          setSelectedClientContactEmails(limited);
                        }}
                        placeholder={t("fields.clientContactEmails.placeholder", {
                          defaultValue: "Select up to 3 contact email(s)...",
                        })}
                        noOptionsMessage={() =>
                          t("fields.clientContactEmails.noOptions", {
                            defaultValue: "No contact emails found for this client.",
                          })
                        }
                        styles={multiSelectHorizontalStyles}
                        classNamePrefix="edit-load-bearer-select"
                        menuPortalTarget={document.body}
                      />

                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                        {t("fields.clientContactEmails.helper", {
                          defaultValue: "These emails come from the client's contact list.",
                        })}
                      </div>
                    </div>
                  )}

                  <div className="edit-load-bearer-field-group">
                    <label className="edit-load-bearer-label">{t("fields.transportCarrier")}</label>
                    <Select
                      isClearable
                      options={carrierOptionsStable}
                      value={selectedTransportOptMemo}
                      onChange={(opt) => {
                        setCarrierId(opt ? toId(opt.value) : "");
                        setTransportCarrierError("");
                        setSelectedSubcontractedEmails([]);
                      }}
                      placeholder={t("placeholders.transportCarrier")}
                      styles={customSelectStyles}
                      classNamePrefix={
                        transportCarrierError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                      }
                    />
                    {transportCarrierError && <p className="edit-load-bearer-error">{transportCarrierError}</p>}
                  </div>

                  {showNotes4subcontracted && (
                    <div className="edit-load-bearer-field-group">
                      <label className="edit-load-bearer-label">
                        {t("fields.subcontractedEmail.label", {
                          defaultValue: "Subcontracted Email",
                        })}
                      </label>

                      <Select
                        isMulti
                        isClearable
                        closeMenuOnSelect={false}
                        options={selectedSubcontractedEmailOptions}
                        value={selectedSubcontractedEmails}
                        onChange={(selectedOptions) => {
                          const limited = Array.isArray(selectedOptions)
                            ? selectedOptions.slice(0, 3)
                            : [];
                          setSelectedSubcontractedEmails(limited);
                        }}
                        placeholder={t("fields.subcontractedEmail.placeholder", {
                          defaultValue: "Select up to 3 subcontracted email(s)...",
                        })}
                        noOptionsMessage={() =>
                          t("fields.subcontractedEmail.noOptions", {
                            defaultValue: "No contact emails found for this subcontracted carrier.",
                          })
                        }
                        styles={multiSelectHorizontalStyles}
                        classNamePrefix="edit-load-bearer-select"
                        menuPortalTarget={document.body}
                      />

                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                        {t("fields.subcontractedEmail.helper", {
                          defaultValue: "These emails come from the subcontracted carrier's contact list.",
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Column 3 - Delivery Points */}
            <div className="edit-load-bearer-column">
              <h3 className="edit-load-bearer-column-title">{t("columns.unloadingPoints")}</h3>

              {deliveryPlaces.map((place, index) => (
                <div key={index} className="edit-load-bearer-multi-location-input">
                  <div className="edit-load-bearer-label-row">
                    <label className="edit-load-bearer-points-label">{t("fields.deliveryPlace")}</label>
                    {deliveryPlaces.length > 1 && (
                      <button
                        type="button"
                        className="edit-load-bearer-delete-btn"
                        onClick={() => removeDeliveryPlace(index)}
                        title={t("actions.removeDeliveryPoint")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="edit-load-bearer-input-row">
                    <div className="edit-load-bearer-select-wrapper">
                      <CreatableSelect
                        isClearable
                        isMulti={false}
                        placeholder={t("placeSelect.placeholder")}
                        value={
                          place.placeId
                            ? placeOptions.find((o) => String(o.value) === String(place.placeId)) || {
                                value: String(place.placeId),
                                label: place.location || t("labels.placeFallback"),
                              }
                            : place.location
                            ? { value: place.location, label: place.location }
                            : null
                        }
                        getOptionValue={(opt) => String(opt.value)}
                        formatOptionLabel={renderOption}
                        options={placeOptions.length ? placeOptions : prefillPlaceOptions}
                        onChange={(selectedOption) => {
                          const updated = [...deliveryPlaces];
                          if (selectedOption?.__isNew__) {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption.label,
                              locationError: false,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                            };
                          } else if (selectedOption?.fullData) {
                            const p = selectedOption.fullData;
                            updated[index] = {
                              ...updated[index],
                              placeId: String(p.id),
                              location: p.placeName,
                              locationError: false,
                              address: p.address,
                              postalCode: p.postalCode,
                              city: p.city,
                              country: p.country,
                              gMapsLink: p.gMapsLink,
                              coordinates: p.coordinates,
                              contactPhone: p.contactPhone,
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                              loadingAccess: updated[index].loadingAccess ?? null,
                              loadingAccessError: false,
                            };
                          } else {
                            updated[index] = {
                              ...updated[index],
                              placeId: null,
                              location: selectedOption?.label || "",
                              locationError: false,
                              address: "",
                              postalCode: "",
                              city: "",
                              country: "",
                              gMapsLink: "",
                              coordinates: "",
                              contactPhone: "",
                              addressError: false,
                              postalCodeError: false,
                              cityError: false,
                              countryError: false,
                              gMapsLinkError: false,
                              coordinatesError: false,
                              contactPhoneError: false,
                            };
                          }
                          setDeliveryPlaces(updated);
                        }}
                        styles={customSelectStyles}
                        classNamePrefix={
                          place.locationError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                        }
                      />
                    </div>
                  </div>

                  {place.locationError && (
                    <p className="edit-load-bearer-error">{t("errors.locationRequired")}</p>
                  )}

                  <div className="edit-load-bearer-datetime-group">
                    <div className="edit-load-bearer-custom-input-wrapper">
                      <input
                        type="date"
                        value={place.date || ""}
                        onChange={(e) => handleDeliveryPlaceChange(e, index, "date")}
                        className={`edit-load-bearer-input ${
                          (invalidFields.deliveries.includes(index) && !place.date) ||
                          dateFieldErrors[`delivery-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                        ref={(el) => {
                          if (el) deliveryDateRefs.current[index] = el;
                        }}
                      />
                      <FaCalendarAlt className="edit-load-bearer-input-icon clickable" />
                    </div>

                    <div className="edit-load-bearer-custom-input-wrapper">
                      <input
                        type="text"
                        placeholder={t("placeSelect.hhmm")}
                        value={place.hour || ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "");
                          let formatted = raw;
                          if (raw.length >= 3) formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;

                          const updated = [...deliveryPlaces];
                          updated[index].hour = formatted;

                          if (!formatted) updated[index].hourError = true;
                          else {
                            const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                            updated[index].hourError = !regex.test(formatted);
                          }

                          setDeliveryPlaces(updated);
                          setGlobalHourFormatError("");
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                          const updated = [...deliveryPlaces];
                          updated[index].hourError = !regex.test(value);
                          setDeliveryPlaces(updated);

                          if (!regex.test(value)) setGlobalHourFormatError(t("errors.invalidHourLong"));
                          else setGlobalHourFormatError("");
                        }}
                        className={`edit-load-bearer-input ${
                          ((hasSubmitted &&
                            ((invalidFields.deliveries.includes(index) && !place.hour) ||
                              invalidDeliveryIndexes.includes(index) ||
                              conflictDateHours.has(`${place.date}|${place.hour}`))) ||
                            place.hourError)
                            ? "input-error"
                            : ""
                        }`}
                        ref={(el) => {
                          if (el) deliveryHourRefs.current[index] = el;
                        }}
                      />
                      <FaClock className="edit-load-bearer-input-icon clickable" />
                    </div>
                  </div>

                  {place.dateError && <p className="edit-load-bearer-error">{t("errors.dateRequired")}</p>}

                  <div className="edit-load-bearer-point-dimensions-block">
                    <label className="edit-load-bearer-label edit-load-bearer-point-dimensions-title">
                      {t("fields.pointDimensions", {
                        defaultValue: "Dimensions of this point",
                      })}
                    </label>

                    <div className="edit-load-bearer-point-dimensions-row">
                      <div className="edit-load-bearer-point-dimension-field">
                        <label>{t("fields.pointLength", { defaultValue: "Length (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.length || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "length")}
                          className={`edit-load-bearer-input ${place.pointLengthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointLengthErrorMessage && (
                          <p className="edit-load-bearer-error">{place.pointLengthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-bearer-point-dimension-field">
                        <label>{t("fields.pointWidth", { defaultValue: "Width (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.width || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "width")}
                          className={`edit-load-bearer-input ${place.pointWidthError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointWidthErrorMessage && (
                          <p className="edit-load-bearer-error">{place.pointWidthErrorMessage}</p>
                        )}
                      </div>

                      <div className="edit-load-bearer-point-dimension-field">
                        <label>{t("fields.pointHeight", { defaultValue: "Height (m)" })}</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={place.height || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "height")}
                          className={`edit-load-bearer-input ${place.pointHeightError ? "input-error" : ""}`}
                          placeholder="0.00"
                        />
                        {place.pointHeightErrorMessage && (
                          <p className="edit-load-bearer-error">{place.pointHeightErrorMessage}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {hasSubmitted && (!place.hour || place.hour.trim() === "") && !place.hourError && (
                    <p className="edit-load-bearer-error">{t("errors.hourRequired")}</p>
                  )}
                  {place.hour && place.hourError && (
                    <p className="edit-load-bearer-error">{t("errors.invalidHourLong")}</p>
                  )}

                  {/* ✅ Loading Access (DELIVERY) */}
                  <label className="edit-load-bearer-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="edit-load-bearer-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) => o.value === String(place.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        place.loadingAccessError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {place.loadingAccessError && (
                    <p className="edit-load-bearer-error">{t("errors.loadingAccessRequired")}</p>
                  )}

                  {/* ✅ Handling Equipment (DELIVERY) */}
                  <label className="edit-load-bearer-label">
                    {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="edit-load-bearer-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(place.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                      isClearable
                      placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        place.handlingEquipmentError ? "edit-load-bearer-select-error" : "edit-load-bearer-select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(place.handlingEquipment || "").toUpperCase() === "OTHER" && (
                    <div className="edit-load-bearer-field-group" style={{ marginBottom: 10 }}>
                      <label className="edit-load-bearer-label">
                        {t("fields.handlingEquipmentOther", { defaultValue: "Other (specify)" })}
                      </label>

                      <input
                        type="text"
                        placeholder={t("fields.handlingEquipmentOtherPlaceholder", {
                          defaultValue: "Write the equipment...",
                        })}
                        value={place.handlingEquipmentOther || ""}
                        onChange={(e) => handleHandlingEquipmentOtherChange(index, e, false)}
                        className={`edit-load-bearer-input ${
                          place.handlingEquipmentOtherError ? "input-error" : ""
                        }`}
                      />
                    </div>
                  )}

                  {/* 🆕 Reference (DELIVERY) - optional */}
                  <div className="edit-load-bearer-field-group" style={{ marginBottom: 10 }}>
                    <label className="edit-load-bearer-label">
                      {t("fields.reference", { defaultValue: "Reference" })}
                    </label>

                    <input
                      type="text"
                      placeholder={t("placeholders.reference", { defaultValue: "Optional reference..." })}
                      value={place.reference || ""}
                      onChange={(e) => handleDeliveryPlaceChange(e, index, "reference")}
                      className="edit-load-bearer-input"
                    />
                  </div>

                  {place.showMoreFields ? (
                    <>
                      <div className="edit-load-bearer-pickup-extra-fields">
                        <input
                          type="text"
                          placeholder={t("placeFields.address")}
                          value={place.address || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "address")}
                          className={`edit-load-bearer-input ${place.addressError ? "input-error" : ""}`}
                        />

                        <div className="edit-load-bearer-row-compact">
                          <div className="edit-load-bearer-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeFields.postalCode")}
                              value={place.postalCode || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "postalCode")}
                              className={`edit-load-bearer-input ${place.postalCodeError ? "input-error" : ""}`}
                            />
                          </div>
                          <div className="edit-load-bearer-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeFields.city")}
                              value={place.city || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "city")}
                              className={`edit-load-bearer-input ${place.cityError ? "input-error" : ""}`}
                            />
                          </div>
                          <div className="edit-load-bearer-input-wrapper">
                            <input
                              type="text"
                              placeholder={t("placeFields.country")}
                              value={place.country || ""}
                              onChange={(e) => handleDeliveryPlaceChange(e, index, "country")}
                              className={`edit-load-bearer-input ${place.countryError ? "input-error" : ""}`}
                            />
                          </div>
                        </div>

                        <input
                          type="text"
                          placeholder={t("placeFields.contactPhone")}
                          value={place.contactPhone || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "contactPhone")}
                          className={`edit-load-bearer-input ${place.contactPhoneError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("placeFields.gMapsLink")}
                          value={place.gMapsLink || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "gMapsLink")}
                          className={`edit-load-bearer-input ${place.gMapsLinkError ? "input-error" : ""}`}
                        />
                        <input
                          type="text"
                          placeholder={t("placeFields.coordinatesPlaceholder")}
                          value={place.coordinates || ""}
                          onChange={(e) => handleDeliveryPlaceChange(e, index, "coordinates")}
                          className={`edit-load-bearer-input ${place.coordinatesError ? "input-error" : ""}`}
                        />
                      </div>

                      <div className="edit-load-bearer-toggle-fields-wrapper">
                        <button
                          type="button"
                          className="edit-load-bearer-toggle-fields-btn"
                          onClick={() => {
                            const updated = [...deliveryPlaces];
                            updated[index].showMoreFields = false;
                            setDeliveryPlaces(updated);
                          }}
                        >
                          {t("actions.hideFields")}{" "}
                          <FiChevronUp className="edit-load-bearer-toggle-arrow" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="edit-load-bearer-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="edit-load-bearer-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...deliveryPlaces];
                          updated[index].showMoreFields = true;
                          setDeliveryPlaces(updated);
                        }}
                      >
                        {t("actions.moreFields")}{" "}
                        <FiChevronDown className="edit-load-bearer-toggle-arrow" />
                      </button>
                    </div>
                  )}

                  {deliveryBeforePickupErrors.date.includes(index) && (
                    <p className="edit-load-bearer-error">{t("errors.deliveryDateBeforePickup")}</p>
                  )}
                  {deliveryBeforePickupErrors.hour.includes(index) &&
                    !deliveryBeforePickupErrors.date.includes(index) && (
                      <p className="edit-load-bearer-error">{t("errors.deliveryHourBeforePickup")}</p>
                    )}
                </div>
              ))}

              {deliveryPlaces.length < MAX_POINTS ? (
                <button type="button" className="edit-load-bearer-add-location-btn" onClick={addDeliveryPlace}>
                  {t("actions.addAnotherDelivery")}
                </button>
              ) : (
                <p className="edit-load-bearer-error-max-points">
                  {t("warnings.maxDeliveryPoints", { max: MAX_POINTS })}
                </p>
              )}
            </div>
          </div>

          {/* 🆕 Notes for Subcontracted (ONLY when carrierId !== companyId) */}
          {showNotes4subcontracted && (
            <div className="edit-load-bearer-field-group">
              <label className="edit-load-bearer-label">{t("notes4subcontracted.label")}</label>
              <textarea
                rows={3}
                placeholder={t("notes4subcontracted.placeholder")}
                value={notes4subcontracted}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.length <= 2000) {
                    setNotes4subcontracted(v);
                    if (notes4subcontractedError) setNotes4subcontractedError("");
                  } else {
                    setNotes4subcontractedError(t("errors.notesMax"));
                  }
                }}
                className={`edit-load-bearer-input ${notes4subcontractedError ? "input-error" : ""}`}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  opacity: 0.75,
                  marginTop: 4,
                }}
              >
                <span>{t("notes.helpersubcontracted")}</span>
                <span>{(notes4subcontracted || "").length}/2000</span>
              </div>
              {notes4subcontractedError && (
                <p className="edit-load-bearer-error">{notes4subcontractedError}</p>
              )}
            </div>
          )}

          {/* Notes for Driver */}
          <div className="edit-load-bearer-notes-row">
            <div className="edit-load-bearer-field-group">
              <label className="edit-load-bearer-label">{t("fields.notesForDriver")}</label>
              <textarea
                rows={3}
                placeholder={t("placeholders.notesForDriver")}
                value={notes4driver}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.length <= 2000) {
                    setNotes4driver(v);
                    if (notes4driverError) setNotes4driverError("");
                  } else {
                    setNotes4driverError(t("errors.notesMaxChars", { max: 2000 }));
                  }
                }}
                className={`edit-load-bearer-input ${notes4driverError ? "input-error" : ""}`}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  opacity: 0.75,
                }}
              >
                <span>{t("notes.helperdriver")}</span>
                <span>
                  {notes4driver.length}/2000
                </span>
              </div>
              {notes4driverError && <p className="edit-load-bearer-error">{notes4driverError}</p>}
            </div>
          </div>

          {conflictError && <p className="edit-load-bearer-error">{conflictError}</p>}

          {/* Buttons */}
          <div className="edit-load-bearer-button-group">
            <button
              type="button"
              onClick={() => navigate("/loads")}
              className="edit-load-bearer-cancel-button"
            >
              {t("actions.cancel")}
            </button>

            <button
              type="submit"
              className="edit-load-bearer-save-button"
              disabled={
                loading ||
                weightError ||
                trailerTypeError ||
                trailerTypeOtherError ||
                priceError ||
                price4subcontractedError
              }
            >
              {loading ? t("actions.saving") : t("actions.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditLoadBearers;